import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { X, Building2, UserPlus, Check } from '../../icons';
import {
  createEvent, patchEvent, patchEventScoped, EVENT_TYPES, TYPE_COLOR_MAP,
  type CalendarEventType, type CalendarEventDto, type EventScope, type CreateEventInput,
  getEventDetail, type ExternalInviteEnvelope, type ExternalInviteInfo,
} from '../../api/calendar';
import { askRecurrenceScope } from './RecurrenceScopeDialog';
// ⭐ 2026-11-02 (지시 57) — 외부 참석자(주소록) + 자동 초대 메일.
import { toast } from '../../components/shared/Toast';
import {
  ensureExternalSupport, noteExternalSupport, picksFromAttendees, rememberExternalSent, externalPatchField, externalCreateField, externalPickKey,
  externalResultNotices, externalUnavailableText, externalDisplayName, type ExternalPick,
} from './lib/externalAttendees';
import './AttendeeContacts.css';
// ⭐ 2026-10-14 반복 규칙 1단계 — 「이후 일정」 새 규칙 미리보기 · 바꾼 칸만 보내기.
import { futureRulePreview } from './recurrence-future-rule';
import { followStartWeekdays } from './recurrence-weekday-follow';
import { pickChangedEditFields } from './edit-changed-fields';
import { sendWithEditConfirms, withEditConfirms, askOverrideConflict, askOverrideLoss, type EditConfirms } from './recurrence-override-conflict';
import { useAttachmentUpload, AttachmentUploader } from '../../components/Attachment';
import { toMessageAttachment } from '../../lib/attachments';
import { Combobox, AutoGrowTextarea } from '../../components/shared';
import { expandToPersonIds, type Picked } from '../../components/shared/EntityPicker';
import { loadOrgPickerTree, type OrgPickerTree } from '../../lib/org-picker-tree';
import { useAuthStore } from '../../store/authStore';
import { RoomStatusModal } from './RoomStatusModal';
import { useMeetingRooms, RoomChoiceSummary } from './RoomChoice';
import { AttendeePickerPanel } from './AttendeePickerPanel';
import { Avatar } from '../../components/shared/Avatar';
import { CalendarErrorNotice } from './CalendarErrorNotice';
import './EventCreateSide.css';
// Date → 피커 값 꼴(YYYY-MM-DDTHH:mm, 현지) — 알림 고르개와 같은 함수(2026-10-19 정리).
import { toPickerValue as formatDatetimeLocal } from './time-label';
import { createReservation, cancelReservation, type ReservationResourceDto } from '../../api/reservation';
import { LocationPicker } from './LocationPicker';
import { DateTimePicker } from './DateTimePicker';
import { EventRangePicker } from './EventRangePicker';
import { allDayWorkHours, ALL_DAY_FALLBACK } from './all-day-work-hours';
import { inferVacationPart, presetChips, useDayWork, type VacationPart } from './type-time-presets';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { layerConfirm } from '../../components/LayerPopup/LayerPopup';
import { listCalendars, type CalendarDto } from '../../api/calendar';
import { tCalendar, getCalendarLang, subscribeCalendarLang } from '../../lib/calendar-i18n';
import { describeCalendarError, friendlyErrorBody, RoomBookingError } from './calendarErrors';
import { isReservationInThePast } from '../../lib/reservation-time';
import { defaultEventRange } from './default-event-range';
// ⭐ 2026-10-13 이식 — 반복 4탭 · 글자 수 · 팀원 모두 초대 (옛 가지 feature/dev-kyeongeun).
import { RecurrenceSettings } from './RecurrenceSettings';
import {
  recurStateFromRule, recurrenceInputFromState, recurSignatureOf, ruleHasDateParts, localDateOf,
  EMPTY_RECUR, type RecurState, turnOnWeekly,
} from './recurrence-form';
import { LengthCounter, LOCATION_MAX, DESCRIPTION_MAX } from './LengthCounter';
import { TeamInviteButton } from './TeamInviteButton';
import { inviteSourceForCalendar } from './calendar-invite-source';
import { useMenuGate } from '../../lib/menu-gate';
import { tCalCreate } from '../../lib/calendar-create-i18n';
// ⭐ 2026-10-14 (반복 규칙 F) — 지난 회차의 「이후 일정」 날짜 잠금 · 「전체 일정」은 오늘 이후부터.
import { isPastOccurrence } from './recurrence-past';

/**
 * 캘린더에서 고를 수 있는 자원 분류 — 「회의실」뿐이다(2026-09-09 사용자 결정).
 * 장비(빔프로젝터 등)는 예약 화면에서만 잡는다.
 * ⚠ **모듈 상수**여야 한다 — 인라인 배열(`categories={['회의실']}`)로 넘기면 매 렌더 새 배열이라,
 *   `RoomPicker` 가 그것을 deps 에 넣는 날 목록을 끝없이 부른다(그 사정은 RoomPicker 머리말에).
 */
const ROOM_CATEGORIES = ['회의실'];

interface EventCreateModalProps {
  initialDate: Date;  // 사용자가 보고 있는 월/날짜 (기본 시작 날짜).
  /**
   * 빈 칸을 «끌어» 만들 때의 정확한 시작·종료 시각 (2026-10-01 · 옵셔널).
   * ⛔ 이것이 없으면 `defaultEventRange`(./default-event-range.ts)가 기본을 준다 —
   *   15:00 에서 끌어도 그 기본으로 열려 «끌어 만들기»의 값이 통째로 사라진다.
   * ⚠ 미지정이면 종전과 100% 같다(회귀 안전).
   */
  initialStart?: Date | null;
  initialEnd?: Date | null;
  onClose: () => void;
  onCreated: () => void;
  /**
   * ⭐ **서버에 «이미 저장됐는데» 뒷일이 실패했을 때** — 달력만 다시 읽어 달라는 신호 (2026-10-01).
   *
   * ⛔ `onCreated` 는 **창을 닫는다**(`setCreating(false)` · `setEditingEvent(null)`). 그래서
   *   실패를 알려야 하는 자리에서는 부를 수 없다 — 부르면 오류 문구가 창과 함께 사라진다.
   * ⇒ ★ 그래서 「닫지 말고 달력만 다시 읽어라」를 따로 둔다. 미지정이면 종전과 100% 같다.
   *
   * ⚠ 언제 불리나: 일정 쓰기(create/patch)는 «됐고» 회의실 예약이 던진 때. 그때 달력을
   *   안 읽으면 사용자는 「저장 실패」로 읽고, 닫아도 그 일정이 «안 보인다»
   *   (2026-10-01 운영: 「닫기를 누르고 새로고침을 누르면 보인다」 — 그 새로고침이 이것이다).
   */
  onRefreshCalendar?: () => void;
  isLeaving?: boolean;
  // T-cal27 — 수정 모드(옵셔널). 지정 시 해당 이벤트로 폼 prefill + PATCH 흐름.
  // 미지정 시 기존 생성 동작 100% 동일(회귀 안전).
  editEvent?: CalendarEventDto | null;
  // 캘린더 상세화(2026-06-07) — 날짜클릭 팝업의 기능메뉴가 type 선지정으로 진입.
  // 'todo'/'anniversary' 면 종일·단일날짜 간소 폼. 미지정/기존 4타입이면 현행 일정 폼.
  presetType?: CalendarEventType;
  /**
   * ⭐ 2026-10-14 (반복 규칙 1단계 B) — 수정하는 회차의 바로 앞·뒤 회차 «보이는 시작»(ISO). 화면이 불러온 범위에서만
   *   알 수 있어 없을 수 있다(그때는 서버가 판정한다). 범위 창의 「이 일정만」·「이후 일정」 잠금에 쓴다.
   */
  occurrenceNeighbors?: { prev: string | null; next: string | null } | null;
}

/**
 * EventCreateModal (P3-CAL-FE-3 + T-cal27) — 이벤트 생성·수정 겸용 modal.
 *
 * 제목/시작/종료/올데이/타입/장소/설명/첨부. editEvent prop 으로 수정 모드 분기:
 *   - 없음: createEvent (기존 동작).
 *   - 있음: 폼 prefill + patchEvent (작성자만, CalendarPage 가 권한 게이트).
 * 참석자·반복·알림은 그룹 G 후속(RecipientPicker/RecurrencePicker).
 *
 * isLeaving: useModalLeave hook 의 unmount transition 단계 (CSS --leaving 토글).
 */
export function EventCreateModal({ initialDate, initialStart = null, initialEnd = null, onClose, onCreated, onRefreshCalendar, isLeaving = false, editEvent = null, presetType, occurrenceNeighbors = null }: EventCreateModalProps) {
  useSyncExternalStore(subscribeCalendarLang, getCalendarLang, getCalendarLang);
  const trapRef = useFocusTrap<HTMLDivElement>(true);

  // 캘린더 선택지 로드. 실패해도 무해 — 선택기가 숨고 기본 캘린더(미지정)로 동작한다.
  // team-calendar: 수정 모드에서도 불러온다(캘린더 이동 지원).
  useEffect(() => {
    let cancelled = false;
    listCalendars()
      .then((cals) => { if (!cancelled) setCalendars(cals); })
      .catch(() => { if (!cancelled) setCalendars([]); });
    return () => { cancelled = true; };
  }, []);
  const isEdit = editEvent != null;
  const [title, setTitle] = useState(() => editEvent?.title ?? '');
  const [type, setType] = useState<CalendarEventType>(() => (editEvent?.type as CalendarEventType) ?? presetType ?? 'meeting');
  // 할일/기념일 = 종일·단일날짜 간소 폼. 기존 4타입 = 현행 일정 폼(회귀 안전).
  const special = type === 'todo' || type === 'anniversary';
  const [isAllDay, setIsAllDay] = useState(() => editEvent?.isAllDay ?? (presetType === 'todo' || presetType === 'anniversary'));
  // todo 완료 / anniversary 매년반복 — special 모드 전용 state.
  const [done, setDone] = useState(() => editEvent?.done ?? false);
  const [repeatYearly, setRepeatYearly] = useState(() => (editEvent ? Boolean(editEvent.recurrenceRule) : true));
  /**
   * ⭐ 기본 시각 — ★ **「지금」을 «한 번만» 센다** (2026-10-01).
   *
   * ⛔ 아래 두 `useState` 가 각자 `defaultEventRange(...)` 를 부르면 **시 경계에서 둘이 갈린다**:
   *   16:59:59.9 에 열면 start 가 17:00 을 잡고, 그 사이 시계가 17:00:00 으로 넘어가 end 가
   *   19:00 을 잡을 수 있다(1시간이 아니라 2시간). ⇒ ★ 그래서 **쌍으로 한 번** 받아 나눠 쓴다.
   * ⚠ `useState` 로 드는 까닭 = **mount 때 딱 한 번** 센다(렌더마다 다시 세지 않는다).
   *   `useMemo` 는 deps 가 Date 객체라 부모가 새 Date 를 주면 다시 센다 — 그러면 창이 열린 뒤
   *   기본값이 움직일 수 있다.
   * ⚠ 수정 모드에서도 세지만 쓰이지 않는다(값은 `editEvent` 가 이긴다) — 셈이 한 번이라 무해하다.
   */
  const [defaultRange] = useState(() => defaultEventRange(initialDate));
  const [startAt, setStartAt] = useState(() =>
    formatDatetimeLocal(editEvent ? new Date(editEvent.startAt) : (initialStart ?? defaultRange.start)),
  );
  const [endAt, setEndAt] = useState(() =>
    formatDatetimeLocal(editEvent ? new Date(editEvent.endAt) : (initialEnd ?? defaultRange.end)),
  );
  /**
   * 시작을 옮기면 종료도 «같은 만큼» 따라간다 — 일정 길이를 지킨다 (2026-10-14 사용자 리포트).
   * ⛔ 종전에는 시작 날짜만 바뀌어 종료가 그 앞에 남았다 ⇒ 「시작은 종료보다 이전이어야 합니다」로 저장이 막혔다
   *   (매주 화요일 회차를 수요일로 옮길 때 「이 일정만」이 안 되는 것처럼 보였다).
   * 종료를 직접 바꾸는 것은 종전 그대로(그때는 길이가 바뀐다).
   */
  function moveStartKeepingLength(nextStart: string) {
    const prevS = new Date(startAt).getTime();
    const prevE = new Date(endAt).getTime();
    const ns = new Date(nextStart);
    setStartAt(nextStart);
    if (Number.isNaN(prevS) || Number.isNaN(prevE) || Number.isNaN(ns.getTime())) return;
    setEndAt(formatDatetimeLocal(new Date(ns.getTime() + Math.max(0, prevE - prevS))));
  }
  const [location, setLocation] = useState(() => editEvent?.location ?? '');
  // team-calendar (2026-07-10) — 어느 캘린더에 넣을지. 팀 캘린더를 고르면 팀원 전원이 본다.
  // 수정 모드에서는 현재 캘린더를 초기값으로 두고, 바꾸면 PATCH 가 이동시킨다('' = 캘린더에서 빼기).
  const [calendars, setCalendars] = useState<CalendarDto[]>([]);
  const originalCalendarId = editEvent?.calendarId ?? '';
  /*
   * ⭐ 시각의 «원본» — `originalCalendarId` 와 같은 관용구다 (2026-09-30).
   *
   * ⛔ 왜 필요한가: 이 모달의 수정은 `scope=all`(master 전체)로 나간다. 그런데 «반복 일정의
   *   회차»를 열어도 같은 모달이 뜨고, 그 회차의 시각이 폼에 들어온다
   *   (`serializeOccurrence` 가 `id: master.id` 에 `startAt: 그 회차 시각`을 실어 주기 때문이다).
   *   ⇒ 그대로 `startAt` 을 보내면 **master 의 DTSTART 가 그 회차로 밀리고**, 앞 회차들이
   *     «지워지는 것이 아니라 아예 펼쳐지지 않는다». 그래서 조용하다.
   *   ⇒ ⇒ 그리고 그것이 **시각을 안 바꿔도** 일어난다 — 제목만 고쳐 저장해도.
   *
   * ⇒ 그래서 «시각을 건드렸을 때만» 보낸다. 안 건드렸으면 세 칸을 통째로 뺀다.
   */
  const originalStartAt = editEvent ? formatDatetimeLocal(new Date(editEvent.startAt)) : null;
  const originalEndAt = editEvent ? formatDatetimeLocal(new Date(editEvent.endAt)) : null;
  const originalIsAllDay = editEvent?.isAllDay ?? null;
  const [calendarId, setCalendarId] = useState<string>(() => originalCalendarId);
  const [description, setDescription] = useState(() => editEvent?.description ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * ⭐ **이 창에서 «이미 만든» 일정의 id** — 방 예약만 실패했을 때 다시 저장하면
   *   일정을 «또» 만들지 않게 들고 있는다 (2026-10-01 · 아래 handleSubmit 의 긴 주석 참조).
   *
   * ⚠ `useState` 가 아니라 `useRef` 다 — 이 값이 바뀌어 다시 그릴 것이 없고,
   *   저장 중에 바뀌는 값이라 렌더 주기에 매달리면 안 된다.
   * ⚠ 창을 닫으면 사라진다(컴포넌트가 unmount 된다) — 그것이 맞다. 다시 열면 새 일정이다.
   */
  const createdEventIdRef = useRef<string | null>(null);

  /**
   * ⛔⛔ **「저장이 안 됐다」가 아니라 «저장은 됐는데 달력이 안 읽혔다»였다** (2026-10-01 운영).
   *
   * ```
   * 사용자 원문: 「저장을 하면 일정이 있거나 시간이 앞이면 저장이 «안 된다».
   *              닫기를 누르고 새로고침을 누르면 «등록한 캘린더에 보인다». 또한 들어있다」
   * ```
   * ⇒ ★ 일정 쓰기는 «됐고» 회의실 예약이 던졌다. 그런데 `onCreated()` 가 **try 안에만** 있어
   *   (이 파일의 두 자리뿐) 던지면 **거기까지 못 간다** ⇒ `refresh()` 가 «안 돌아» 달력이 그대로다.
   *   ⇒ ⇒ 사용자는 「저장 실패」로 읽고, 새로고침해야 그 일정을 본다.
   *
   * ⇒ ★ 그래서 **「서버에 쓰기가 들어갔나」를 이 창이 기억**하고, catch 에서 그때만 달력을 다시 읽는다.
   * ⚠ `createdEventIdRef` 로는 모자란다 — **수정(patch)** 도 같은 일을 겪는데 그쪽은 id 를 안 만든다.
   * ⚠ 제출마다 «처음에 꺼 둔다» — 지난번 저장의 기억으로 이번 실패를 판단하면 안 된다.
   */
  const serverWriteLandedRef = useRef(false);


  /*
    참석자 초대 (2026-09-28) — 「체크를 켜야 나온다」.
    까닭(사용자 원문): 「선택할지 말지도 모르는데 다 보여주고 있다」.

    ⛔ **수정 모드에서는 «읽기 전용»이다.** 서버 `patchBodySchema`(calendar-helpers.ts:195)가
      `attendeeUserIds` 를 **안 받고** `additionalProperties:false` 라, 보내도 400 이 아니라
      **조용히 지워진다** — 「저장했는데 참석자가 안 바뀐다」가 되고 오류조차 안 뜬다.
      · 만료 조건 — PATCH 가 `attendeeUserIds` 를 받게 되면.
      · 그때 할 일 — 이 읽기 전용을 풀고, 「이미 나 외 참석자가 있으면 «켜진 채»로 연다」를 넣는다
        (그 규격은 사용자가 이미 정해 뒀고, 서버가 없어 보류된 것뿐이다).

    ⚠ 조직도는 **3+N 호출**이다(팀마다 getTeam). 그래서 `org-picker-tree` 의 규약이
      「게으르게 — 필요할 때만」인데, **체크를 켤 때 부르는 것**이 그 규약과 정확히 맞물린다.
  */
  /*
   * ⭐ 2026-10-15 — 위 만료 조건이 «발동했다»: PATCH 가 `attendeeUserIds` 를 받는다(사용자 결정 — 참석자 수정은
   *   날짜·시간 수정과 같은 범위 규칙). ⇒ 수정 모드도 만들기와 같은 고르개를 쓰고, 지금 명단(그 회차의 명단)을 채워 연다.
   *   만든 사람 말고 다른 참석자가 있으면 «켜진 채»로 연다.
   */
  const originalAttendeePicked = useMemo<Picked[]>(
    () => (editEvent?.attendees ?? []).map((a) => ({ kind: 'person' as const, id: String(a.userId), name: a.name || String(a.userId) })),
    [editEvent],
  );
  const [attendeesOn, setAttendeesOn] = useState(() => Boolean(
    editEvent && editEvent.attendees.some((a) => String(a.userId) !== String(editEvent.creatorId)),
  ));
  const [attendees, setAttendees] = useState<Picked[]>(() => (isEdit ? originalAttendeePicked : []));
  const [orgTree, setOrgTree] = useState<OrgPickerTree | null>(null);
  const [orgError, setOrgError] = useState(false);

  /*
   * ⭐ 2026-11-02 (지시 57) — 외부 참석자(주소록 연락처). 저장하면 서버가 초대 메일을 «자동으로» 보낸다(체크 칸 없음).
   *   · 서버가 이 기능을 모르면(옛 판) 숨기고 종전처럼 동작한다 — 수정은 상세 GET 의 `externalInvite` 로, 새 일정은 한 번 묻는다.
   *   · 수정 모드의 «원래 명단»을 모르면(extOriginal === null) 절대 보내지 않는다 — 모르는 채 보내면 남의 명단을 지운다.
   *   · 외부 참석자만 있는 일정도 참석자 칸이 «켜진 채» 보이게 extOn 을 따로 둔다(attendeesOn 은 직원 명단의 스위치로 남는다).
   */
  const [extSupported, setExtSupported] = useState(false);
  const [extOriginal, setExtOriginal] = useState<ExternalPick[] | null>(() => (isEdit ? null : []));
  const [ext, setExt] = useState<ExternalPick[]>([]);
  const [extOn, setExtOn] = useState(false);
  const [extInfo, setExtInfo] = useState<Pick<ExternalInviteInfo, 'available' | 'reason'> | null>(null);
  const peopleOn = attendeesOn || extOn;
  const extNow = peopleOn ? ext : [];
  useEffect(() => {
    let alive = true;
    let detail: ReturnType<typeof getEventDetail> | null = null;
    try {
      detail = editEvent ? getEventDetail(editEvent.id) : null;
    } catch {
      return () => { alive = false; };   // 상세를 부를 수 없는 판 — 외부 참석자는 숨긴 채
    }
    if (editEvent && detail) {
      detail
        .then((d) => {
          if (!alive || !noteExternalSupport(d)) return;
          const picks = picksFromAttendees(d.externalAttendees);
          setExtSupported(true);
          setExtOriginal(picks);
          setExt(picks);
          setExtInfo(d.externalInvite ?? null);
          rememberExternalSent(editEvent.id, d.externalAttendees);
          if (picks.length > 0) setExtOn(true);
        })
        .catch(() => { /* 못 읽으면 외부 참석자는 숨긴 채(보내지 않는다) — 일정 수정은 종전 그대로 된다. */ });
    } else {
      void ensureExternalSupport().then((ok) => { if (alive) setExtSupported(ok); });
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /** 반복 일정을 고치는 중 — 외부 참석자는 시리즈 전체 단위라 「이 일정만」으로는 못 바꾼다(서버 400). */
  const extSeriesNote = isEdit && Boolean(editEvent?.recurrenceRule) ? tCalendar('calendar.external.locked_series') : null;
  const EXTERNAL_MAX = 50;

  // 프롭을 새로 뚫지 않는다 — `CalendarSidebar` 가 같은 자리에서 스토어를 직접 읽는 선례를 따른다.
  const me = useAuthStore((s) => s.user);
  const selfIdStr = me?.id != null ? String(me.id) : null;

  /** 「나」 한 줄 — 참석자의 기본값. 이름이 비면 칩이 빈 채로 보이므로 메일·id 로 받친다. */
  const mePicked = useMemo<Picked[]>(() => {
    if (me?.id == null) return [];
    const id = String(me.id);
    return [{ kind: 'person', id, name: me.name || me.email || id }];
  }, [me?.id, me?.name, me?.email]);

  /**
   * 보낼 사람 id — 팀째 고른 것은 «그 안 사람으로» 펼친다.
   * ⛔ 손으로 펼치지 않는다. `expandToPersonIds` 가 하위 트리까지 훑고, 비활성 사용자를 빼고,
   *   Set 이라 겹친 사람을 한 번만 센다(팀에 「나」가 들어 있어도 중복이 안 난다).
   */
  const attendeeUserIds = useMemo(
    () => (attendeesOn ? expandToPersonIds(attendees, orgTree?.groups ?? []) : []),
    [attendeesOn, attendees, orgTree],
  );

  /**
   * ⛔ 서버 상한 100명(`calendar-helpers.ts:152` `maxItems: 100`).
   *   팀을 통째로 고르면 쉽게 넘는데, 그냥 보내면 400 이 나고 사용자는 «몇 명이 넘었는지»를 모른다.
   *   그래서 «펼친 뒤에» 세어 보내기 직전에 막고, 수와 함께 「어떻게 하면 되나」를 말한다.
   */
  const ATTENDEE_MAX = 100;
  const attendeeOver = attendeeUserIds.length > ATTENDEE_MAX;
  /** 나 말고 한 사람이라도 있나 — 팀째 고른 것도 펼쳐서 본다. 일정 창의 고른 결과 요약과 옆 창을 닫을 때 끄기가 같이 쓴다. */
  const hasOthers = attendeeUserIds.some((id) => id !== selfIdStr);

  /** 체크를 켤 때만 조직도를 읽는다. 끄면 값을 «나만»으로 되돌린다(숨기는 게 아니다). */
  /*
    회의실 예약 (2026-10-01) — 정본의 배선을 옮겼다. 「장소」 칸 안의 카드 단추(`calendar-optin-card`)로 켠다(2026-10-14).

    ⭐ 수정 모드의 초깃값은 «지금 살아 있는 예약»의 자원이다. 서버가 취소·반려 건은 빼고 주므로
      여기 오는 것은 대기중이거나 확정된 예약뿐이다(EVENT_PUBLIC_SELECT 의 where).
  */
  const linkedReservation = editEvent?.reservations?.[0] ?? null;
  const originalRoomId = linkedReservation?.resourceId ?? '';
  const [roomId, setRoomId] = useState<string>(() => originalRoomId);
  const [roomOn, setRoomOn] = useState<boolean>(() => !!originalRoomId);
  /**
   * ⭐ 2026-10-19 (사용자 결정 · 시안 확정안) — 회의실·참석자는 일정 창 «오른쪽에 나란히» 뜨는 옆 창에서 고른다.
   *   일정 창 모양은 그대로이고, 고르면 카드 아래에 고른 내용 + 「수정」만 붙는다. 옆 창은 한 번에 하나.
   */
  const [side, setSide] = useState<'room' | 'people' | null>(null);
  const { rooms: meetingRooms, error: roomsError } = useMeetingRooms(roomOn, ROOM_CATEGORIES);
  const pickedRoom = roomId ? meetingRooms?.find((r) => r.id === roomId) ?? null : null;
  // 회의실이 하나도 없으면(예약 기능 미도입) 옆 창을 열 것이 없다 — 종전처럼 직접 적는 칸만 남는다.
  useEffect(() => {
    if (side === 'room' && meetingRooms && meetingRooms.length === 0) setSide(null);
  }, [side, meetingRooms]);
  /** ⭐ 2026-10-16 — 회의실 고르개가 「예약 불가」를 미리 보는 시간. 저장 때 잡는 그 구간(startAt ~ endAt)이다. */
  const roomCheckRange = useMemo(() => {
    const s = new Date(startAt);
    const e = new Date(endAt);
    return Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()) || !(s < e) ? null : { start: s, end: e };
  }, [startAt, endAt]);

  /**
   * ⛔⛔ **회의실을 켜고 «지난 시간»으로 저장하면 «일정만» 저장됐다** (2026-10-01 운영 육안 확인).
   *
   * ```
   *   ① createEvent 로 일정이 «만들어진다»
   *   ② bookRoom 이 IN_THE_PAST 로 거절된다 (서버 reservation-helpers.ts:184 — `end <= now`)
   *   ⇒ ⛔ 「이미 지난 시간은 예약할 수 없습니다」가 뜨는데 «일정은 남는다»
   *      사용자 판단(2026-10-01): 「저장되면 안 된다」
   * ```
   * ⇒ ★ 그래서 **저장 단추를 «막는다»**. 누르고 나서 멈추면 「눌렀는데 아무 일도 안 났다」로 읽힌다.
   *   ⇒ 그리고 못 누르는 단추는 **왜 못 누르는지 보여야** 한다 — footer 위에 안내 줄을 둔다.
   *
   * ⚠ 판정은 `lib/reservation-time` 한 곳에서 온다 — 예약 화면과 서버가 쓰는 그 잣대다.
   *   ★ `start` 가 아니라 `end` 라서 «진행 중인» 시간대는 잡을 수 있다(흔한 조작을 막지 않는다).
   * ⚠ 회의실을 «안 켰으면» 막지 않는다 — 지난 일정을 적어 두는 것은 정상이다(기록·회고).
   *   ⇒ ★ 막는 것은 «예약이 걸릴 때»뿐이다. 그 경계를 대조군 시험이 잠근다.
   *
   * ## ⛔⛔ 1판은 **«수정»에서 너무 넓게 막았다** (2026-10-01 · 앱 쪽 실측이 알려 줬다)
   *
   * 1판의 조건은 `!special && roomOn && !!roomId` 였다. 그런데 **수정 모드는 방이 걸린 일정을
   * 열면 `roomOn` 이 «켜진 채로» 시작한다**(아래 `originalRoomId` 에서 온다).
   * ```
   *   ⇒ ⛔ 지난 회의의 «제목만» 고쳐도 저장이 막혔다 — 그 길은 bookRoom 을 «부르지도 않는다»
   *   ⇒ ★ 바로 위 주석이 「막는 것은 예약이 걸릴 때뿐」이라 적어 두었는데 코드가 달랐다
   * ```
   * ⇒ ★ 「막는 쪽」으로 틀린 것이다(새는 쪽이 아니다) — **정상 조작을 막았다.** 그래서 더 나쁘다.
   * ⇒ ⇒ ⭐ 그래서 **「새 예약을 걸 자리인가」를 `roomChanged` 한 곳에서** 센다. 그 값은 아래
   *   handleSubmit 의 **실제 예약 분기가 쓰는 그 값**이다 — 같은 뜻을 두 벌로 적지 않는다.
   *
   * ⚠ 수정에서 **시각만** 바꾸면(방은 그대로) 막지 않는다. 그 길은 예약을 다시 걸지 않으므로
   *   서버의 `IN_THE_PAST` 에 닿지 않는다. ⇒ ★ 다만 그러면 «예약 시각이 안 따라간다» —
   *   그것은 이 판 밖의 별개 간극이고, 관제에 올렸다.
   */
  /** 저장하면 «방이 바뀌나» — 새로 걸거나(roomId 있음) 옛것을 놓는다(roomId 빈 값). */
  const roomChanged = !special && roomId !== originalRoomId;
  /** 저장하면 «새 예약을 거나» — 지난 시간 막기의 전제다. */
  const willBookRoom = roomChanged && roomOn && !!roomId;
  /**
   * ⭐ 2026-10-16 — 이 일정 시간이 «이미 지났나»(잣대는 아래 roomPastBlocked 와 같다 — 종료 시각 기준).
   *   지났으면 회의실 카드를 «켤 수 없다»(사용자 지적 「지난 날짜의 일정에서 회의실 예약 버튼을 눌러도 활성화가 돼」).
   *   켜진 채로 지난 시간이 되면 방 목록이 모두 「예약 불가」가 된다. 끄는 것은 언제나 된다.
   */
  const roomTimePast = !special
    && !Number.isNaN(new Date(endAt).getTime())
    && isReservationInThePast(new Date(endAt), new Date());
  const roomPastBlocked = willBookRoom
    && !Number.isNaN(new Date(endAt).getTime())
    && isReservationInThePast(new Date(endAt), new Date());

  /**
   * ⭐ **「종료 시각」 칸을 «가리킨다»** — 초점을 옮기고 한 번 두드러지게 한다 (2026-10-02).
   *
   * ⚠ 앱(AOS)은 「시간 선택 «화면»으로 보낸다」인데 웹은 그 칸이 **같은 창 안**이다.
   *   ⇒ ★ 그래서 «보내기»가 아니라 ⭐ «가리키기»다. 화면을 옮기면 사용자가 돌아올 길을 잃는다.
   * ⚠ 두드러짐은 **잠깐**이다 — 남겨 두면 「여기가 틀렸다」는 표시로 굳어 보인다.
   *   ⇒ 그래서 타이머로 끈다. ⛔ 창이 닫힌 뒤 setState 하지 않게 **unmount 에서 치운다.**
   * ⚠ jsdom 에는 `scrollIntoView` 가 «없다» — 있는지 보고 부른다(없으면 초점만 옮긴다).
   */
  const [pointedEnd, setPointedEnd] = useState(false);
  const pointTimerRef = useRef<number | null>(null);
  useEffect(() => () => {
    if (pointTimerRef.current !== null) window.clearTimeout(pointTimerRef.current);
  }, []);
  function pointAtEndTime() {
    const el = document.getElementById('event-end');
    el?.focus();
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
    setPointedEnd(true);
    if (pointTimerRef.current !== null) window.clearTimeout(pointTimerRef.current);
    pointTimerRef.current = window.setTimeout(() => {
      pointTimerRef.current = null;
      setPointedEnd(false);
    }, 1500);
  }

  /** 「회의실이 장소다」 — 고른 방 이름이 그대로 이 일정의 장소가 된다. 해제하면 비워 직접 적게. */
  function handleRoomPick(r: ReservationResourceDto | null) {
    setLocation(r ? r.name : '');
  }
  function toggleRoom(next: boolean) {
    setRoomOn(next);
    // ⚠ 끄면 «되돌린다» — 숨기기만 하면 안 보이는 방이 함께 예약된다.
    if (!next) { setRoomId(''); handleRoomPick(null); }
    // 켜면 옆 창(현황 표)을 연다 · 끄면 그 옆 창을 닫는다.
    setSide((cur) => (next ? 'room' : cur === 'room' ? null : cur));
  }

  /**
   * 고른 방을 이 일정에 건다. 남은 자리 판정은 예약 서버가 자원 행을 잠근 채 하고,
   * 자리가 없으면 409 로 답한다 — 화면에서 미리 계산하지 않는다.
   * ⭐ 일정은 이미 저장된 뒤라, 실패해도 「저장 실패」가 아니라 「방만 못 잡았다」로 알린다
   *   (`RoomBookingError` → `describeCalendarError(…, 'room')` → 「일정은 저장했지만 …」).
   */
  async function bookRoom(eventId: string, resourceId: string, s: Date, e2: Date, occurrenceAt: string | null): Promise<void> {
    try {
      await createReservation({
        resourceId, startAt: s, endAt: e2,
        calendarEventId: eventId,
        occurrenceAt,
        purpose: title.trim() || null,
      });
    } catch (err) {
      throw new RoomBookingError(err);
    }
  }

  /**
   * 고르개가 쓸 조직도를 «아직 없을 때만» 읽는다 — 켤 때(toggleAttendees)와 켜진 채 열린 수정 화면(아래 효과)이 같이 쓴다.
   * @param isAlive 받은 뒤 아직 반영해도 되나(효과의 정리 뒤에는 버린다). 미지정이면 늘 반영.
   */
  function ensureOrgTree(isAlive: () => boolean = () => true) {
    if (orgTree !== null) return;
    setOrgError(false);
    void loadOrgPickerTree()
      .then((t) => { if (isAlive()) setOrgTree(t); })
      .catch(() => { if (isAlive()) setOrgError(true); });
  }

  function toggleAttendees(next: boolean) {
    setAttendeesOn(next);
    // ⭐ 2026-10-15 — 수정 모드에서 켜면 «지금 명단»에서 시작한다(만들기는 종전대로 「나」부터).
    setAttendees(isEdit && next && originalAttendeePicked.length > 0 ? [...originalAttendeePicked] : [...mePicked]);
    // ⭐ 2026-11-02 (지시 57) — 외부 참석자도 같은 스위치다: 켜면 원래 명단(만들기는 없음)에서, 끄면 비운다(= 모두 빼기).
    setExtOn(false);
    setExt(next ? [...(extOriginal ?? [])] : []);
    if (next) ensureOrgTree();
    setSide((cur) => (next ? 'people' : cur === 'people' ? null : cur));
  }

  // ⭐ 2026-10-15 — 수정 모드가 «켜진 채»로 열리면 고르개가 쓸 조직도를 곧바로 읽는다(만들기는 켤 때 읽는다).
  useEffect(() => {
    if (!isEdit || !attendeesOn) return;
    let alive = true;
    ensureOrgTree(() => alive);
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * ⭐ 2026-10-21 사용자 지시 — 옆 창이 «고르지 않은 채» 닫히면 그 카드도 끈다.
   *   회의실: 「회의실 예약하기」로 방을 정하지 않았으면(roomId 없음) 끈다 — 켜진 채면 예약된 줄 안다.
   *   참석자: 나 말고 아무도 없으면 끈다.
   *   닫기·× 뿐 아니라 다른 옆 창으로 바뀌는 것(회의실 → 참석자)도 «닫힘»이라 옆 창 값의 변화로 본다.
   *   이미 정해 둔 것이 있으면(수정 모드·「수정」으로 다시 연 경우) 그대로 둔다.
   *   회의실이 하나도 없어 저절로 닫힌 것(위 효과)은 사용자가 닫은 게 아니라 손대지 않는다.
   */
  const prevSideRef = useRef(side);
  useEffect(() => {
    const prev = prevSideRef.current;
    prevSideRef.current = side;
    if (prev === side) return;
    // ⚠ toggleRoom(false) 를 쓰지 않는다 — 그쪽은 장소를 비운다. 방을 안 골랐으면 장소는 사용자가 적은 것이라 남긴다.
    if (prev === 'room' && roomOn && !roomId && !(meetingRooms && meetingRooms.length === 0)) setRoomOn(false);
    // ⭐ 2026-11-02 (지시 57D) — 외부 참석자도 «다른 사람»이다. 외부만 골랐으면 끄지 않는다(끄면 외부 명단까지 비운다).
    if (prev === 'people' && attendeesOn && !hasOthers && extNow.length === 0) toggleAttendees(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [side]);

  /*
   * 반복 — ⭐ 2026-10-13 이식: 4탭(매일·매주·매월·매년) · 매주 요일 · 매월/매년 두 방식 · 수정 화면에서도 바꾼다.
   *   (옛 가지 ee33d27d7 · 21f8b4e23 · ec36d8a56 · e9255f050 · 6dba1d0ad)
   * ⚠ 수정 화면은 «지금 규칙»을 채워서 연다 — 매주 요일은 서버가 UTC 기준으로 적었을 수 있어
   *   그 일정의 시작 시각으로 «현지 요일»로 되돌린다(`recurStateFromRule`).
   */
  const [recur, setRecur] = useState<RecurState>(() => (
    editEvent ? recurStateFromRule(editEvent.recurrenceRule, editEvent.startAt) : { ...EMPTY_RECUR }
  ));
  /** 처음 연 상태의 지문 — 「반복 설정을 바꿨나」를 이것과 견준다. */
  const [initialRecurSig] = useState(() => recurSignatureOf(recur));
  /** ⭐ 2026-10-15 — 처음 연 반복 상태(시작 날짜를 옮길 때 매주 요일을 따라 바꾸는 기준). */
  const [initialRecur] = useState(() => recur);
  /** ⭐ 2026-10-14 (E-3) — 「횟수」로 저장된 일정을 열었나. 그 일정에만 옛 「횟수」 선택지를 남긴다. */
  const [legacyCount] = useState(() => recur.end === 'count');
  const recurFreq = recur.freq;
  const alreadyRecurring = isEdit && Boolean(editEvent?.recurrenceRule);
  /*
   * ⭐ 2026-10-15 (사용자 요청) — 이미 반복인 일정의 수정 화면에서 시작 날짜를 옮기면 매주 요일 체크도 같은 일수만큼
   *   따라간다(recurrence-weekday-follow.ts 머리말). 사용자가 요일을 직접 바꿔 «지난번에 따라 바꾼 값»과 달라졌으면 손대지 않는다.
   */
  const followedRecurSigRef = useRef(initialRecurSig);
  useEffect(() => {
    if (!alreadyRecurring) return;
    setRecur((cur) => {
      if (recurSignatureOf(cur) !== followedRecurSigRef.current) return cur;
      const next = { ...cur, byDay: followStartWeekdays(initialRecur, originalStartAt, startAt).byDay };
      followedRecurSigRef.current = recurSignatureOf(next);
      return recurSignatureOf(next) === recurSignatureOf(cur) ? cur : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startAt]);

  // 팀원 모두 초대 — 업무(task) 메뉴가 꺼져 있으면 단추가 없다(서버가 그 메뉴 아래서만 팀원을 준다).
  const taskGate = useMenuGate('task');
  const inviteSource = useMemo(
    () => inviteSourceForCalendar(calendars.find((c) => c.id === calendarId), { taskMenuEnabled: taskGate.enabled }),
    [calendars, calendarId, taskGate.enabled],
  );

  const typeOptions = useMemo(
    () => EVENT_TYPES.map((t) => ({ value: t, label: TYPE_COLOR_MAP[t].label })),
    [],
  );

  // 첨부 — pattern-shared-component-extraction-cross-domain-7-atomic 의 *N=3 정착 사례*.
  // chat MessageInput / mail ComposeModal 과 동일 hook + 컴포넌트 사용 (cross-domain 정착 검증).
  const uploadState = useAttachmentUpload();
  const { attachments, uploadingFile, clearAttachments } = uploadState;

  useEscapeClose(handleClose, submitting);

  /** ⭐ 2026-11-02 (지시 57) — 저장 응답의 외부 참석자 결과를 사람 말로 알린다(코드·칸 이름 없이). 변화가 없으면 조용하다. */
  function announceExternal(env: ExternalInviteEnvelope) {
    for (const n of externalResultNotices(env)) {
      if (n.variant === 'error') toast.error(n.text);
      else toast.info(n.text);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting || uploadingFile) return;  // 업로드 중에는 send 불가 (chat/mail 동일 패턴)
    if (!title.trim()) {
      setError(tCalendar('calendar.event_create.err_title'));
      return;
    }
    const start = new Date(startAt);
    const end = new Date(endAt);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      setError(tCalendar('calendar.event_create.err_time_format'));
      return;
    }
    if (start >= end) {
      setError(tCalendar('calendar.event_create.err_start_after_end'));
      return;
    }
    // ⛔ 100명 상한 — «펼친 뒤» 수로 막는다. 팀을 고르면 칩은 하나인데 사람은 수십일 수 있다.
    if (attendeeOver) {
      setError(tCalendar('calendar.event_create.err_attendee_max', { max: ATTENDEE_MAX, n: attendeeUserIds.length }));
      return;
    }
    /*
     * ⛔ 단추를 막았는데도 «여기서 한 번 더» 본다 — 띠·허리띠 둘이다.
     *   까닭: 글자 칸에서 Enter 를 누르면 브라우저가 폼을 submit 하는 길이 있고,
     *   disabled 단추가 그것을 늘 막아 주지는 않는다. ⇒ 막힌 상태로 저장이 새면
     *   «일정만 저장되는» 그 결함이 그대로 돌아온다.
     */
    if (roomPastBlocked) {
      // ⚠ 위 안내와 «같은 글자»에서 온다 — 한 상황에 두 말이 생기면 어느 쪽이 참인지 모른다.
      setError(`${tCalendar('calendar.room.past_reason')} ${tCalendar('calendar.room.past_action')}`);
      pointAtEndTime();
      return;
    }
    /*
     * ⭐ 시각을 «건드렸나» — 폼 글자끼리 견준다(Date 로 견주면 같은 시각이 다른 글자여도 같다고 나온다).
     *   AOS 가 같은 자리에서 쓰는 `timeChanged` 와 같은 뜻이다(안드로이드3 · saveServerEdit).
     * ⚠ 만들기(생성)에서는 늘 참이다 — 원본이 없으므로.
     */
    const timeChanged = !isEdit
      || startAt !== originalStartAt
      || endAt !== originalEndAt
      || (special ? true : isAllDay) !== originalIsAllDay;

    setSubmitting(true);
    serverWriteLandedRef.current = false;   // ⚠ 지난 저장의 기억으로 이번을 판단하지 않는다
    setError(null);
    // ⭐ 2026-11-02 (지시 57) — 저장 응답의 외부 참석자 칸(초대 메일 «보내는 중» 등)을 받아 두었다가 저장이 끝나면 알린다.
    let extEnv: ExternalInviteEnvelope = {};
    // 외부 참석자와 닿는 저장일 때만 손잡이를 넘긴다 — 아니면 저장 함수 호출이 종전과 «글자 그대로» 같다.
    const keepExternal = extSupported && ((extOriginal?.length ?? 0) > 0 || extNow.length > 0)
      ? (env: ExternalInviteEnvelope) => { extEnv = env; }
      : undefined;
    try {
      if (isEdit && editEvent) {
        /*
         * ⭐⭐ **반복 회차를 수정하면 «어디까지»인지 먼저 묻는다** (2026-09-30 사용자 운영 확인).
         *
         * 사용자 원문: 「매주 반복된 일정을 등록하고 해당 일정을 수정하려고 했는데 «전체가 수정된다».
         *              수정은 «하나씩» 되어야 한다」
         *
         * ⛔ 종전에는 늘 `scope=all`(master 전체)로 나갔다. 회차를 열어 제목만 고쳐도 **모든 회차의
         *   제목이 바뀌었다** — 그리고 시각을 바꾸면 서버가 400 으로 막아 «아예 못 고쳤다».
         *
         * ★ 물을 것은 이미 다 있었다 — 배선만 없었다:
         * ```
         *   askRecurrenceScope        모듈 export · RecurrenceAction 에 'edit' «있음»
         *   문구 여섯(title/desc/confirm/hint×3)_edit                        «있음»
         *   patchEventScoped          «있음» · 호출자 0  ← 이 줄이 첫 호출자다
         *   삭제·끌어 옮기기는 같은 대화상자를 이미 쓴다(CalendarPage:617·662)
         * ```
         * ⭐⭐ **2026-10-06 — 이제 «셋 다» 뜬다**(`scopesFor` 가 늘 `SCOPES`). 종전 이 자리에
         *   적혀 있던 「둘만 뜬다 · 「전체 일정」은 빠져 있다 · UNTIL 을 안 밀어 마지막 회차가
         *   사라진다」는 ★ **모두 낡았다**:
         * ```
         * UNTIL 밀기   ✅ planSeriesShift 가 민다(shiftRruleUntil)
         * BYDAY 밀기   ✅ rewriteBydayFromDtstart(머리말 ⓐ)
         * 앞 회차 소멸  ✅ occurrenceAt 이 오면 delta 만큼만 민다(머리말 ⓖ — 날짜 집합으로 쟀다)
         * ```
         * ⇒ ★ 그래서 아래에서 scope='all' 도 `patchEventScoped` 로 보내 **`occurrenceAt` 을 싣는다** —
         *   ⛔ 안 실으면 「전체 일정 ＋ 시각 변경」이 400 이다(아래 긴 주석).
         */
        const occAt = editEvent.occurrenceAt ?? null;
        /*
         * ⭐ 2026-10-13 — 반복 설정을 «보낼» 때:
         *   ① 사용자가 반복 설정을 바꿨다(지문이 다르다)
         *   ② 날짜로 정한 반복(몇째 주 요일·말일)인데 날짜가 바뀌었다 — 옛 규칙을 그대로 밀면 회차가 틀린다
         *      (종전 서버가 400 으로 막았다 — 그 거절은 걷혔다). 그래서 «새 날짜에서 다시 센» 규칙을 함께 보낸다
         *      (옛 가지 106c858a2 「몇째 주 무슨 요일도 재계산」).
         */
        // ⭐ 2026-10-15 — 시작 날짜를 따라 «저절로» 바뀐 매주 요일은 바꾼 것으로 치지 않는다(서버가 같은 규칙을 만든다).
        const recurChanged = !special && recurSignatureOf(recur) !== initialRecurSig
          && recurSignatureOf(recur) !== recurSignatureOf(followStartWeekdays(initialRecur, originalStartAt, startAt));
        /*
         * ⭐ 2026-10-14 (반복 규칙 1단계 B · 사용자 확정) — ② 를 걷었다. 날짜를 옮긴 「이후 일정」의 새 규칙은
         *   이제 «서버가» 원래 규칙의 형태를 지켜 만든다(여러 요일은 같은 일수만큼 · 몇째 주는 다시 셈 · 말일 등).
         *   화면이 옛 반복 상태로 규칙을 다시 만들어 보내면(예: 화·목을 그대로 실음) 그 규칙이 우선해 틀린다.
         *   ⇒ 반복 설정은 사용자가 «직접 바꿨을 때만» 보낸다(그 값이 우선 — 기존 recurrence 전송 경로).
         */
        const recurrenceToSend = recurChanged ? recurrenceInputFromState(recur, startAt) : undefined;
        /*
         * ⭐ 2026-10-14 (1단계 B) — 날짜·시간을 바꿨나(하루종일 켜고 끄기만 한 것은 제외 — 같은 날짜 안의 시각 맞춤).
         * ⭐ 2026-10-14 (반복 규칙 F) — 「전체 일정」은 더 잠그지 않는다: 날짜·시간·반복을 바꾸면 지난 회차는 그대로 두고
         *   오늘 이후 첫 회차부터 바뀐다(서버) ⇒ 그 범위 아래 안내만 바꿔 보인다. 대신 지난 회차의 「이후 일정」을 잠근다.
         */
        const allDayToggled = (special ? true : isAllDay) !== originalIsAllDay;
        const sameLocalDates = localDateOf(startAt) === localDateOf(originalStartAt ?? '')
          && localDateOf(endAt) === localDateOf(originalEndAt ?? '');
        const dateTimeChanged = (startAt !== originalStartAt || endAt !== originalEndAt)
          && !(allDayToggled && sameLocalDates);
        /*
         * ⭐ 2026-10-15 (사용자 결정) — 참석자를 바꿨나. 만들 때와 같은 규칙(아무도 없으면 만든 사람)으로 «실제 명단»을 견준다
         *   — 만든 사람만 있던 일정을 고르개를 안 켠 채 저장하면 바뀐 것이 아니다.
         */
        const effectiveAttendees = (ids: string[]) => [...new Set(ids.length > 0 ? ids : [String(editEvent.creatorId)])].sort().join(',');
        const attendeesChanged = !special
          && effectiveAttendees(attendeeUserIds) !== effectiveAttendees(editEvent.attendees.map((a) => String(a.userId)));
        // ⭐ 2026-11-02 (지시 57) — 외부 참석자(바꿨을 때만 «전체 명단»). 원래 명단을 모르면 보내지 않는다.
        const extField = extSupported && !special ? externalPatchField(extOriginal, extNow) : undefined;
        let scope: EventScope = 'all';
        if (editEvent.recurrenceRule && occAt) {
          /*
           * ⛔ 서버가 «미리» 막는 조합만 잠근다(`patchEventScoped` 머리말):
           *   this   + 캘린더 이동      → 400 CALENDAR_MOVE_SCOPE_INVALID (캘린더는 master 속성)
           *   future + 시작을 앞당김    → 400 (잘라낸 앞 시리즈와 겹친다)
           * ⚠ 「고른 회차 뒤에 따로 고친 회차가 있음」(409)은 **이 창이 잴 수 없다** —
           *   다른 회차 목록을 갖고 있지 않다. 그건 서버가 409 를 주고
           *   `describeCalendarError` 가 사람 말로 알린다(RECURRENCE_FUTURE_HAS_EXCEPTIONS).
           */
          const disabled: Partial<Record<EventScope, string>> = {};
          if (calendarId !== originalCalendarId) {
            disabled.this = tCalendar('calendar.recurrence.this_calendar_move_blocked');
          }
          // ⭐ 2026-11-02 (지시 57) — 외부 참석자는 시리즈 전체 단위다(서버 400 EXTERNAL_ATTENDEES_SERIES_ONLY).
          if (extField) {
            disabled.this ??= tCalendar('calendar.external.this_scope_blocked');
          }
          /*
           * ⭐ 2026-10-14 (반복 규칙 1단계 B · 사용자 확정) — 날짜 이동의 범위 규칙:
           *   이 일정만 — 직전 회차보다 뒤 · 다음 회차보다 앞 (서버 400 OCCURRENCE_ORDER_VIOLATION)
           *   이후 일정 — 직전 회차보다 뒤면 앞당기기도 된다 (서버 400 RECURRENCE_FUTURE_START_BEFORE_PREV)
           *   전체 일정 — ⭐ 2026-10-14 (F) 지난 회차는 그대로 두고 오늘 이후 첫 회차부터 바뀐다(잠그지 않고 안내만)
           *   ⚠ 이웃 회차는 화면이 불러온 범위 안에서만 안다(occurrenceNeighbors) — 모르면 서버가 판정한다.
           *   종전 「시작을 앞당기면 이후 일정 잠금」(future_earlier_blocked)은 이 규칙으로 대체됐다.
           */
          if (dateTimeChanged) {
            const prevAt = occurrenceNeighbors?.prev ? Date.parse(occurrenceNeighbors.prev) : NaN;
            const nextAt = occurrenceNeighbors?.next ? Date.parse(occurrenceNeighbors.next) : NaN;
            // 직전 회차 «이하»는 두 범위가 함께 본다 — 한 번만 센다.
            const atOrBeforePrev = !Number.isNaN(prevAt) && start.getTime() <= prevAt;
            if (atOrBeforePrev) {
              disabled.future = tCalendar('calendar.recurrence.future_before_prev_blocked');
            }
            if (atOrBeforePrev || (!Number.isNaN(nextAt) && start.getTime() >= nextAt)) {
              disabled.this ??= tCalendar('calendar.recurrence.this_order_blocked');
            }
          }
          // ⭐ 2026-10-13 — 반복 설정 변경은 「이 일정만」으로 할 수 없다(서버 RECURRENCE_SCOPE_INVALID).
          if (recurrenceToSend) {
            disabled.this = tCalendar('calendar.recurrence.this_recurrence_change_blocked');
          }
          /*
           * ⭐ 2026-10-14 (반복 규칙 F · 사용자 확정) — 날짜·시간·반복 설정(하루종일 전환 포함)을 바꿀 때:
           *   이후·전체 — 지난 회차(시작의 현지 날짜 < 오늘)에서 열었으면 지난 회차는 그대로 두고 오늘 이후 일정부터 바뀐다
           *     ⇒ 그 아래 안내를 그 말로 바꾼다(2026-10-15 — 「이후」 잠금을 걷었다).
           *   이 일정만 — 지난 회차라도 잠그지 않는다(순서 규칙만 — 사용자 정정).
           */
          // ⭐ 2026-10-15 (사용자 결정) — 참석자 변경은 날짜·시간 변경과 같은 범위 규칙을 따른다.
          const timeOrRecurChanged = dateTimeChanged || allDayToggled || Boolean(recurrenceToSend) || attendeesChanged;
          const hints: Partial<Record<EventScope, string>> = {};
          if (timeOrRecurChanged) {
            /*
             * ⭐ 2026-10-15 (사용자 결정) — 지난 회차에서 연 「이후 일정」도 잠그지 않는다. 「전체」처럼 지난 회차는 그대로 두고
             *   오늘 이후 일정부터 바뀐다(서버가 같은 길로 처리) ⇒ 같은 안내를 보이고, 앞당기기·순서 잠금도 걸지 않는다.
             */
            if (isPastOccurrence(occAt)) {
              // ⭐ 2026-10-15 (사용자 결정 · 같은 날 되돌림) — 「이 일정만」은 지난 회차도 날짜·시간·참석자를 바꿀 수 있다(순서 규칙만).
              delete disabled.future;
              hints.future = tCalendar('calendar.recurrence.hint_all_from_today');
            }
            hints.all = tCalendar('calendar.recurrence.hint_all_from_today');
          }
          // ⭐ 2026-10-14 — 「이후 일정」을 고르면 새 반복 규칙을 사람 말로 미리 보인다(날짜·시간을 바꾼 경우).
          const notes: Partial<Record<EventScope, string>> = {};
          if (dateTimeChanged && !recurrenceToSend && !special) {
            const text = futureRulePreview(editEvent.recurrenceRule, new Date(occAt), start);
            if (text) notes.future = text;
          }
          const picked = await askRecurrenceScope({
            action: 'edit',
            title: editEvent.title,
            ...(Object.keys(disabled).length > 0 ? { disabled } : {}),
            ...(Object.keys(notes).length > 0 ? { notes } : {}),
            ...(Object.keys(hints).length > 0 ? { hints } : {}),
          });
          // ⛔ 취소·ESC·배경 클릭이면 «아무것도 저장하지 않는다». 물어 놓고 답을 버리면 취소가 저장이 된다.
          if (!picked) return;
          scope = picked;
        }
        // 수정 — PATCH. 첨부 변경은 그룹 G 후속이라 미포함.
        /*
         * ⭐ 2026-10-14 (반복 규칙 1단계 A · 사용자 확정)
         *   · 설명·장소를 «비워서» 저장하면 비워진다 — 원래 값이 있었는데 비웠으면 빈 문자열을 보낸다
         *     (종전에는 빈 값을 안 보내 원래 값이 남았다). 원래도 비었으면 키를 안 보낸다.
         *   · 반복 일정의 「이 일정만」·「전체 일정」은 «바꾼 칸만» 보낸다 — 「이 일정만」은 회차 덮어쓰기에
         *     바꾼 칸만 남고, 「전체」는 바꾼 칸만 나뉜 시리즈 전부에 간다(다른 조각의 다른 값을 덮지 않는다).
         */
        const descTrim = description.trim();
        const descOrig = (editEvent.description ?? '').trim();
        const descField = descTrim || (descOrig ? '' : undefined);
        const locTrim = location.trim();
        const locOrig = (editEvent.location ?? '').trim();
        const locField = special ? undefined : (locTrim || (locOrig ? '' : undefined));
        const body = {
          title: title.trim(),
          description: descField,
          /*
           * ⛔ 셋을 «함께» 넣거나 «함께» 뺀다 — 하나만 빼면 안 된다.
           *   서버 `validatePatchStartEnd` 는 온 것과 «master 의 기존 값»을 섞어 본다:
           *     newStart = updateData.startAt ?? event.startAt
           *   ⇒ `endAt` 만 가면 그 검사는 통과하고 **master 의 길이만 조용히 바뀐다**.
           *     400 이 아니라 «틀린 저장»이라 더 나쁘다.
           */
          ...(timeChanged ? {
            startAt: start.toISOString(),
            endAt: end.toISOString(),
            isAllDay: special ? true : isAllDay,
          } : {}),
          type,
          ...(type === 'todo' ? { done } : {}),
          location: locField,
          // team-calendar — 바뀐 경우에만 보낸다('' = 캘린더에서 빼기). 안 바뀌었으면 키 자체를 생략.
          ...(calendarId !== originalCalendarId ? { calendarId } : {}),
          // ⭐ 2026-10-13 — 반복 설정(바꿨을 때만). 안 바꿨으면 키 자체가 없다(종전 요청과 같다).
          ...(recurrenceToSend ? { recurrence: recurrenceToSend } : {}),
          // ⭐ 2026-10-15 — 참석자(바꿨을 때만 «전체 명단»). 안 바꿨으면 키 자체가 없다.
          ...(attendeesChanged ? { attendeeUserIds } : {}),
          // ⭐ 2026-11-02 (지시 57) — 외부 참석자(바꿨을 때만). 안 바꿨으면 키 자체가 없다(서버가 그대로 둔다).
          ...(extField ? { externalAttendees: extField } : {}),
        };
        /*
         * ⛔⛔ **2026-10-06 — 'all' 도 `occurrenceAt` 을 «보낸다».** 종전에는 'all' 이 `patchEvent`
         * 로 가서 쿼리를 한 글자도 안 붙였고, 그것이 ★ 「전체 일정」을 되살릴 때 ⭐ «가장 조용한
         * 함정»이었다:
         * ```
         * 서버  if (occForAll === null && isRecurring && movesTime) → 400
         *       OCCURRENCE_AT_REQUIRED_FOR_TIME_CHANGE
         * ⇒ ⛔ 즉 occurrenceAt 을 안 보내면 ★ 「전체 일정 ＋ 시각 변경」이 «통째로 400» 이다
         *   (제목만 바꾸면 movesTime=false 라 통과한다 — ⚠ 그래서 «절반만» 깨져 더 못 찾는다)
         * ```
         * ⇒ ★ 그리고 보내야 «맞게» 돈다 — 서버는 occurrenceAt 이 오면 `planSeriesShift` 로
         *   master 를 **delta 만큼만** 밀고 UNTIL·BYDAY·예외 키를 함께 민다. 안 보내면 그 길을
         *   아예 못 탄다. ⇒ ⇒ 머리말 ⓖ 가 그것이다.
         *   ⭐ 2026-10-14 (반복 규칙 F) — 지금 서버는 그 회차에서 «옮긴 만큼»을 재어, 지난 회차는 그대로 두고
         *   오늘 이후 첫 회차부터 새 시리즈로 바꾼다(통째로 밀기는 걷혔다). occurrenceAt 이 필요한 것은 같다.
         *
         * ⚠ 「바이트 단위로 같다」는 성질은 ★ 그대로 지켜진다 — `scopeParams` 가 scope='all' 이고
         *   occurrenceAt 이 없으면 `undefined` 를 주고, `patchEventScoped` 는 그때 `patchEvent` 로
         *   돌아간다. ⇒ 반복이 아닌 일정·회차를 모르는 master 의 요청은 전과 한 바이트도 다르지 않다.
         *   (scope 키는 'all' 일 때 «안 붙는다» — 서버가 `req.query.scope ?? 'all'` 로 읽는다.)
         */
        // ⭐ 2026-10-14 — 반복 회차의 「이 일정만」·「전체 일정」은 바꾼 칸만(edit-changed-fields.ts 머리말).
        const changedOnly = Boolean(editEvent.recurrenceRule && occAt) && scope !== 'future';
        const sendBody = changedOnly ? pickChangedEditFields(body, editEvent) : body;
        if (changedOnly && Object.keys(sendBody).length === 0) {
          // 바뀐 칸이 없다 — 서버에 빈 요청(400 NO_FIELDS_TO_UPDATE)을 보내지 않고 저장된 것으로 본다.
        } else {
          // ⭐ 2026-10-14 (반복 규칙 F) — E-4 의 「지난 날짜 추가」 확인 창(409 재전송)은 걷었다 — 「전체」는 오늘 이후부터라 물을 것이 없다.
          // ⭐ 2026-10-14 (반복 규칙 G) — '이 일정만' 수정 내역에 닿으면(409) 묻는다(recurrence-override-conflict.ts 머리말). 닫기 = 저장 안 함.
          //   409 는 「전체」·「이후」에서만 오므로 「이 일정만」은 종전 그대로 한 번에 간다.
          // 두 저장 길은 «보내는 함수»만 다르다 — 확인 묻기는 한 자리에서.
          const send = scope === 'all' && !occAt
            // 반복이 아니거나 «회차를 모르는» master — 종전 길 그대로. 서버에 보낼 회차가 아예 없다.
            ? (confirms: EditConfirms) => (keepExternal
              ? patchEvent(editEvent.id, withEditConfirms(sendBody, confirms), keepExternal)
              : patchEvent(editEvent.id, withEditConfirms(sendBody, confirms)))
            : (confirms: EditConfirms) => (keepExternal
              ? patchEventScoped(editEvent.id, withEditConfirms(sendBody, confirms), { scope, occurrenceAt: occAt ?? undefined }, keepExternal)
              : patchEventScoped(
                editEvent.id,
                withEditConfirms(sendBody, confirms),
                { scope, occurrenceAt: occAt ?? undefined },
              ));
          if (!(await sendWithEditConfirms(send, askOverrideConflict, askOverrideLoss))) return;
        }
        serverWriteLandedRef.current = true;

        /*
          회의실 변경 — 새 방을 **먼저** 잡고 옛 예약을 놓는다 (정본과 같은 차례).
          ⛔ 반대로 하면 새 예약이 실패했을 때 «원래 잡아 둔 방까지» 잃는다.
            자원이 서로 다르므로 자기 자신과 겹치지 않는다.
          ⚠ 옛 예약 취소가 실패하는 것은 저장을 막을 사유가 아니다 — 예약 화면에서 지울 수 있다.
        */
        // ⭐ 잣대는 위 `roomChanged` 한 곳에서 온다 — 저장 단추를 막는 판정이 같은 값을 쓴다.
        if (roomChanged) {
          if (roomId) {
            await bookRoom(editEvent.id, roomId, start, end, editEvent.occurrenceAt ?? null);
          }
          if (linkedReservation) {
            try {
              await cancelReservation(linkedReservation.id);
            } catch { /* 위 주석 — 저장을 막지 않는다. */ }
          }
        }
      } else {
        // 반복 입력 빌드: 기념일=매년(YEARLY) 자동, 그 외 special=반복없음, 일정=사용자 선택.
        /*
         * ⭐ 2026-10-13 — 화면 상태 → 서버 입력은 `recurrenceInputFromState` 한 곳에서 센다.
         *   기념일(매년)도 같은 셈을 탄다 — 2/29 기념일은 평년에 2/28 로(옛 가지 ec36d8a56), 현지 날짜도 함께 간다.
         */
        const recurrence = special
          ? (type === 'anniversary' && repeatYearly ? recurrenceInputFromState({ ...EMPTY_RECUR, freq: 'YEARLY' }, startAt) : undefined)
          : recurrenceInputFromState(recur, startAt);
        /*
         * ⛔⛔ **다시 저장하면 일정이 «두 개» 생겼다** (2026-10-01).
         *
         * ```
         * 회의실이 꽉 찬 시간에 일정을 만들면
         *   ① createEvent 로 일정이 «만들어진다»
         *   ② bookRoom 이 409 로 던진다 ⇒ 「일정은 저장했지만 방을 못 잡았다」가 뜬다
         *   ③ ⛔ 사용자가 «다시 저장»을 누르면 이 블록이 처음부터 돌아 일정이 «또» 만들어진다
         * ```
         * ⇒ ★ `created.id` 를 기억하지 않아서다. 그 안내는 「일정은 저장했다」고 말하는데
         *   코드는 그 사실을 «잊었다» — 말과 코드가 갈라진 자리다.
         * ⇒ ⇒ 그래서 만든 id 를 들고 있다가, 재시도면 **만들지 않고 고치고 방만 다시 잡는다**.
         *
         * ⚠ 기억은 «이 창이 열려 있는 동안»만이다 — 닫으면 사라진다(ref 라 렌더도 안 돈다).
         *   닫았다 다시 열면 새 일정을 만드는 것이 맞다. 그 일정은 이미 달력에 있다.
         *
         * ⛔ **남는 구멍 하나를 적어 둔다**: 재시도의 `patchEvent` 는 서버 `patchBodySchema` 가
         *   받는 칸만 실어 보낸다(title·description·startAt·endAt·isAllDay·type·done·location·
         *   calendarId). ⇒ ★ 재시도 «전에» 참석자·첨부·반복을 바꿨다면 그 변경은 «안 간다».
         *   · 까닭 — 그 셋은 PATCH 가 아예 안 받는다(보내도 ajv 가 조용히 지운다).
         *   · 만료 조건 — PATCH 가 `attendeeUserIds`·`attachments`·`recurrence` 를 받게 되면.
         *   · 그때 할 일 — 아래 patchEvent 에 그 셋을 더한다. 시험이 그 구멍을 못 박아 두었다.
         */
        if (createdEventIdRef.current) {
          serverWriteLandedRef.current = true;   // 재시도도 «이미 있는» 일정을 고치는 것이다
          await patchEvent(createdEventIdRef.current, {
            title: title.trim(),
            description: description.trim() || undefined,
            startAt: start.toISOString(),
            endAt: end.toISOString(),
            isAllDay: special ? true : isAllDay,
            type,
            ...(type === 'todo' ? { done } : {}),
            location: special ? undefined : (location.trim() || undefined),
            ...(calendarId ? { calendarId } : {}),
            // ⭐ 2026-10-15 — 위 만료 조건(참석자)이 발동했다 — PATCH 가 참석자를 받는다. 재시도 전에 바꾼 명단도 간다.
            ...(!special ? { attendeeUserIds } : {}),
          });
          if (!special && roomId) {
            await bookRoom(createdEventIdRef.current, roomId, start, end, recurrence ? start.toISOString() : null);
          }
          createdEventIdRef.current = null;   // 방까지 잡혔으면 잊는다
          clearAttachments();
          onCreated();
          return;
        }
        const createInput: CreateEventInput = {
          title: title.trim(),
          description: description.trim() || undefined,
          startAt: start.toISOString(),
          endAt: end.toISOString(),
          isAllDay: special ? true : isAllDay,
          type,
          ...(type === 'todo' ? { done } : {}),
          location: special ? undefined : (location.trim() || undefined),
          recurrence,
          attachments: !special && attachments.length > 0 ? attachments.map(toMessageAttachment) : undefined,
          // 참석자 — 체크가 꺼져 있으면 키 자체를 안 보낸다(종전 동작 그대로 = 참석자 0).
          ...(attendeeUserIds.length > 0 ? { attendeeUserIds } : {}),
          // 미지정('') 이면 서버가 소유자 기본 캘린더로 폴백(기존 동작).
          ...(calendarId ? { calendarId } : {}),
          // ⭐ 2026-11-02 (지시 57) — 외부 참석자. 없거나 서버가 모르면 키 자체를 안 보낸다.
          ...(extSupported && !special && externalCreateField(extNow)
            ? { externalAttendees: externalCreateField(extNow) }
            : {}),
        };
        const created = await (keepExternal ? createEvent(createInput, keepExternal) : createEvent(createInput));
        /*
          회의실은 일정이 생긴 **뒤에** 잡는다 — 예약이 일정 id 를 가리키기 때문이다.
          ⚠ 반복 일정은 «첫 회차»만 잡는다(예약은 한 구간에 하나다) — 그 사실을 카드에 미리 적는다.
        */
        // ⭐ 방을 잡기 «전»에 기억한다 — 잡다가 던지면 catch 로 가므로, 뒤에 적으면 안 기억된다.
        createdEventIdRef.current = created.id;
        serverWriteLandedRef.current = true;
        if (!special && roomId) {
          await bookRoom(created.id, roomId, start, end, recurrence ? start.toISOString() : null);
        }
        createdEventIdRef.current = null;   // 끝까지 됐으면 잊는다
      }
      announceExternal(extEnv);
      clearAttachments();
      onCreated();
    } catch (err) {
      // ⭐ 2026-11-02 (지시 57) — 일정은 들어갔는데 뒤(회의실 등)가 실패해도 메일은 이미 «보내는 중»일 수 있다 — 그것도 알린다.
      if (serverWriteLandedRef.current) announceExternal(extEnv);
      /*
       * ⭐ **서버 코드·서버 문구를 그대로 붙이지 않는다** (2026-09-30).
       *
       * 종전 줄은 `[코드] 서버문구` 였다. 그것이 지금 «브라우저» 사용자에게 이렇게 보인다:
       * ```
       *   [OCCURRENCE_AT_REQUIRED_FOR_TIME_CHANGE] 반복 일정의 시각을 「전체 일정」으로 바꾸려면
       *   앱을 업데이트해 주십시오. 「이 회차만」과 「이후 모두」는 지금도 됩니다.
       * ```
       * ⛔ 웹에는 «업데이트할 앱이 없고», 이 창에는 «범위 고르개가 아예 없다» — 두 문장이 다 틀렸다.
       *   (서버 문구는 «옛 APK» 사용자에게 한 말이다 — calendar.routes.ts 「남는 불편은 옛 AOS APK 뿐」)
       *
       * ⇒ ★ 그래서 «한 코드만» 덮지 않고 **전부** `describeCalendarError` 로 보낸다.
       *   한 갈래만 사람 말로 바꾸면 「어떤 실패는 사람 말 · 어떤 실패는 코드」가 되어 지금보다 나쁘다
       *   (`calendarErrors.ts` 머리말의 경고). 그 표에 없는 코드는 여전히 서버 한국어 문구를 «사유»로
       *   쓰므로(「모르는 거절」 갈래), 이 바꿈으로 «설명이 사라지는» 코드는 없다.
       *
       * ⏸ 저장 «전» 네 검사(제목·시각·순서·참석자 수)는 «건드리지 않았다» — 이 판을 되돌릴 때
       *   갈라야 하기 때문이다. 그쪽은 `clientError`(지금 부르는 곳 0)와 `CalendarErrorNotice` 로
       *   옮기는 별도 판이다. 만료 조건 — `clientError` 의 호출자가 0 이 아니게 되면.
       */
      /*
       * ⛔⛔ **쓰기는 «들어갔는데» 달력이 안 읽히던 것** (2026-10-01 운영 · 위 ref 의 긴 주석).
       *
       * `onCreated()` 는 **try 안에만** 있어서, 회의실 예약이 던지면 거기까지 못 가고
       * `refresh()` 가 «안 돈다» ⇒ 일정은 서버에 있는데 달력에는 없다.
       * ⇒ ★ 그래서 쓰기가 들어간 때만 **달력을 다시 읽는다**. 창은 «안 닫는다» — 오류를 보여야 한다.
       *
       * ⛔ `finally` 에 두지 않았다 — 거기 두면 **일정 만들기 «자체»가 실패한 때도** 돈다.
       *   그때는 만들어진 것이 «없으니» 읽을 까닭이 없다(해롭진 않지만 틀린 말이 된다).
       * ⚠ 그리고 오류 문구는 그대로다 — `describeCalendarError` 가 `RoomBookingError` 를
       *   ctx 'room' 으로 보내 「일정은 저장했지만 회의실은 예약하지 못했습니다」를 이미 말한다.
       *   ⇒ ★ 새 글자를 안 지었다. 말과 화면이 그제야 같아진다.
       */
      if (serverWriteLandedRef.current) onRefreshCalendar?.();

      const fe = describeCalendarError(err, isEdit ? 'edit' : 'create');
      setError(`${fe.title} — ${friendlyErrorBody(fe)}`);
    } finally {
      setSubmitting(false);
    }
  }

  /*
   * ⭐ 2026-10-19 (사용자 지시) — 작성하다가 창 «바깥»을 누르면 바로 닫지 않고 묻는다.
   *   「작성 내역이 있습니다. 작성을 취소하시겠습니까?」 — 처음 연 모습에서 하나라도 바뀌었을 때만(안 바꿨으면 종전대로 닫힘).
   *   비교 기준은 «처음 그린 순간»의 입력값이다(수정 창이면 원래 일정 값). 닫기 단추·Esc 는 종전 그대로.
   */
  const formSig = JSON.stringify([
    title, type, isAllDay, done, repeatYearly, startAt, endAt, location, calendarId, description,
    attendeesOn, attendees.map((p) => `${p.kind}:${p.id}`), roomOn, roomId, recurSignatureOf(recur),
    attachments.length, Boolean(uploadingFile),
  ]);
  const [initialFormSig] = useState(() => formSig);
  const confirmingCloseRef = useRef(false);
  async function handleBackdropClose() {
    if (submitting || confirmingCloseRef.current) return;
    // ⭐ 2026-11-02 (지시 57) — 외부 참석자만 바꿨어도 «고친 것»이다(그 명단은 늦게 와서 formSig 에 넣지 않았다).
    const extDirty = extOriginal !== null && externalPatchField(extOriginal, extNow) !== undefined;
    if (formSig !== initialFormSig || extDirty) {
      confirmingCloseRef.current = true;
      try {
        const ok = await layerConfirm(tCalendar('calendar.event_create.discard_confirm'), {
          confirmText: tCalendar('calendar.event_create.discard_yes'),
          cancelText: tCalendar('calendar.event_create.discard_no'),
          danger: true,
        });
        if (!ok) return;
      } finally {
        confirmingCloseRef.current = false;
      }
    }
    handleClose();
  }

  // 닫기 시 inflight upload abort + state reset.
  function handleClose() {
    if (submitting) return;
    clearAttachments();
    onClose();
  }

  // 하루종일 = 그날 출근 ~ 퇴근(근태 시간 · 2026-10-20 사용자 결정. 종전 2026-10-14 = 00:00 ~ 23:59).
  //   켜는 순간에는 기본 09:00 ~ 18:00 으로 두고, 바로 아래 효과가 그 날짜의 내 근무시간을 읽어 맞춘다.
  //   끌 때는 그대로 둔다 — 사용자가 시간 칸에서 다시 고른다. 체크박스와 달력 창의 「하루종일」 스위치가 함께 쓴다.
  function applyAllDay(checked: boolean) {
    setIsAllDay(checked);
    if (checked) {
      setVacPart(null); // 연차(종일)면 반차·반반차 잠금을 푼다
      setStartAt(joinDt(splitDt(startAt).date, ALL_DAY_START));
      setEndAt(joinDt(splitDt(endAt).date, ALL_DAY_END));
    }
  }
  /*
   * ⭐ 2026-10-20 — 하루종일을 켜거나, 하루종일인 채 날짜를 바꾸면 그 날짜의 «내 근태 시간»(출근 ~ 퇴근)을 읽어 맞춘다
   *   (all-day-work-hours.ts). 처음 연 값은 건드리지 않는다 — 수정 창을 열기만 해도 «바뀐 것»이 되면 안 된다.
   *   저장 때 서버도 «작성자» 기준으로 같은 규칙으로 맞춘다(calendar-allday-hours.ts).
   */
  const allDayKey = !special && isAllDay ? `${splitDt(startAt).date}|${splitDt(endAt).date}` : '';
  const allDayKeySeen = useRef(allDayKey);
  useEffect(() => {
    if (allDayKey === allDayKeySeen.current) return;
    allDayKeySeen.current = allDayKey;
    if (!allDayKey) return;
    const [sd = '', ed = ''] = allDayKey.split('|');
    let alive = true;
    void allDayWorkHours(sd, ed).then((h) => {
      if (!alive) return;
      setStartAt((cur) => (splitDt(cur).date === sd ? joinDt(sd, h.start) : cur));
      setEndAt((cur) => (splitDt(cur).date === ed ? joinDt(ed, h.end) : cur));
    });
    return () => { alive = false; };
  }, [allDayKey]);

  /*
   * ⭐ 2026-10-20 — 유형별 추천 시간(시안 E · type-time-presets.ts). 달력 창 시작·종료 칸 아래 칩 줄로 그린다.
   *   회의 = 길이(끝 = 시작 + 길이) · 외부 = 오전/오후(내 근무시간) · 휴가 = 반차/반반차(시작만 · 종료 잠금)/연차(종일).
   *   휴가 반차·반반차를 고르면 종료는 «시작 + 길이»로 잠기고, 시작을 옮기면 길이를 지켜 따라간다(moveStartKeepingLength).
   */
  const [vacPart, setVacPart] = useState<VacationPart | null>(() => (
    editEvent && editEvent.type === 'vacation' ? inferVacationPart(startAt, endAt, !!editEvent.isAllDay) : null
  ));
  useEffect(() => { if (type !== 'vacation') setVacPart(null); }, [type]);
  const dayWork = useDayWork(splitDt(startAt).date, !special && (type === 'external' || type === 'vacation'));
  const endLocked = type === 'vacation' && vacPart !== null && !isAllDay;
  const typePresets = special ? null : presetChips(type, { startAt, endAt, isAllDay, work: dayWork, vacPart });
  const pickerPresets = typePresets && {
    label: typePresets.label,
    chips: typePresets.chips.map((c) => ({
      key: c.key,
      label: c.label,
      on: c.on,
      onPick: () => {
        if (c.allDay) { applyAllDay(true); return; }
        if (isAllDay) setIsAllDay(false);
        if (c.startAt) setStartAt(c.startAt);
        if (c.endAt) setEndAt(c.endAt);
        setVacPart(c.vacPart ?? null);
      },
    })),
  };

  // special(할일/기념일) 모드의 단일 날짜 — start 00:00 ~ end 23:59 동일일.
  const dateValue = startAt.slice(0, 10);
  function setDate(d: string) {
    if (!d) return;
    setStartAt(`${d}T00:00`);
    setEndAt(`${d}T23:59`);
  }

  // 제목 = 타입별(날짜팝업 메뉴명과 일치): 일정/할일/기념일 + 추가/수정.
  const headingKey = isEdit
    ? (type === 'todo' ? 'calendar.event_edit.heading_todo'
        : type === 'anniversary' ? 'calendar.event_edit.heading_anniversary'
          : type === 'vacation' ? 'calendar.event_edit.heading_vacation'
            : 'calendar.event_edit.heading')
    : (type === 'todo' ? 'calendar.event_create.heading_todo'
        : type === 'anniversary' ? 'calendar.event_create.heading_anniversary'
          : type === 'vacation' ? 'calendar.event_create.heading_vacation'
            : 'calendar.event_create.heading_event');
  const heading = tCalendar(headingKey);

  /* ⭐ 2026-10-19 — 오른쪽 옆 창. 회의실 = 현황 표(RoomStatusModal docked) · 참석자 = AttendeePickerPanel. */
  const sidePanel = special ? null
    : side === 'room' && roomOn && meetingRooms && meetingRooms.length > 0 && roomCheckRange ? (
      <RoomStatusModal
        rooms={meetingRooms}
        category={ROOM_CATEGORIES.length === 1 ? ROOM_CATEGORIES[0] : undefined}
        eventRange={roomCheckRange}
        initialRoomId={roomId}
        ignoreReservationId={linkedReservation?.id ?? null}
        allDay={isAllDay}
        onChoose={(room, s, e) => {
          setRoomId(room.id);
          handleRoomPick(room);
          // 표에서 시각까지 골랐으면 일정 시간도 그것으로(종전 RoomPicker 의 onPickTime 과 같다). 하루종일은 방만.
          if (!isAllDay && (s.getTime() !== roomCheckRange.start.getTime() || e.getTime() !== roomCheckRange.end.getTime())) {
            setStartAt(formatDatetimeLocal(s));
            setEndAt(formatDatetimeLocal(e));
          }
          setSide(null);
        }}
        onClose={() => setSide(null)}
      />
    ) : side === 'people' && peopleOn && !orgError ? (
      <AttendeePickerPanel
        // 조직도가 늦게 오면 그때 다시 그린다 — 처음 펼칠 팀·이름표가 조직도에 매달려 있다.
        key={orgTree ? 'ready' : 'loading'}
        groups={orgTree?.groups ?? []}
        loose={orgTree?.loose ?? []}
        loading={orgTree === null}
        value={attendees}
        selfId={selfIdStr}
        selfName={me?.name ?? null}
        max={ATTENDEE_MAX}
        renderToolbar={inviteSource ? ({ existingIds, add }) => (
          <TeamInviteButton
            source={inviteSource}
            existingIds={existingIds}
            selfId={selfIdStr}
            max={ATTENDEE_MAX}
            onAdd={add}
            disabled={submitting}
          />
        ) : undefined}
        // ⭐ 2026-11-02 (지시 57) — 서버가 외부 참석자를 알 때만 「주소록」 탭이 붙는다(모르면 종전 그대로).
        external={extSupported ? { value: ext, max: EXTERNAL_MAX } : undefined}
        onApply={(people, picked) => {
          setAttendees(people);
          if (picked) {
            setExt(picked);
            setExtOn(picked.length > 0);
          }
          // 외부 참석자만 있던 일정에서 고르기를 마쳤으면 직원 명단도 «켜진» 것으로 본다(고른 그대로 보낸다).
          if (!attendeesOn) setAttendeesOn(true);
          setSide(null);
        }}
        onClose={() => setSide(null)}
      />
    ) : null;

  return (
    <div
      className={`calendar-modal-backdrop${isLeaving ? ' calendar-modal-backdrop--leaving' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget && !isLeaving) void handleBackdropClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={heading}
      aria-hidden={isLeaving || undefined}
    >
      {/* ⭐ 2026-10-19 — 일정 창과 옆 창(회의실 현황 · 참석자 고르기)을 나란히 감싼다. 포커스 가두기도 둘을 함께 본다. */}
      <div ref={trapRef} className={`calendar-modal-duo${sidePanel ? ' has-side' : ''}`}>
      <div className={`calendar-modal${isLeaving ? ' calendar-modal--leaving' : ''}`}>
        <header className="calendar-modal-header">
          <h2>{heading}</h2>
          <button
            type="button"
            className="calendar-modal-close"
            onClick={handleClose}
            disabled={submitting}
            aria-label={tCalendar('calendar.btn.close')}
          >
            <X size={18} />
          </button>
        </header>

        <form className="calendar-modal-body" onSubmit={handleSubmit}>
          <div className="calendar-modal-field">
            {/* ⭐ 2026-10-13 — 라벨 줄 오른쪽에 «n/한도»(옛 가지 c59fee059·c8a7df61f · 제목 200 = 사용자 결정). */}
            <div className="cal-lblrow">
              <label htmlFor="event-title">{tCalendar('calendar.event_create.label_title')}</label>
              <LengthCounter id="event-title-count" length={title.length} max={EVENT_TITLE_MAX} />
            </div>
            <input
              id="event-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={tCalendar('calendar.event_create.placeholder_title')}
              aria-describedby="event-title-count"
              maxLength={EVENT_TITLE_MAX}
              required
              autoFocus
            />
          </div>

          {/*
            ⭐ 2026-10-14 — 일정 창 배치를 6100(옛 가지 feature/dev-kyeongeun) 화면에 맞췄다(사용자 지시).
              캘린더 | 유형 한 줄 → 시작/종료(날짜 + 시간 두 피커, 세로) → 하루종일 · 반복 한 줄(반복 패널은 그 아래)
              → 장소(회의실 예약 카드 + 직접 입력) → 참석자(초대 카드) → 설명 → 첨부파일.
            ⚠ 저장·검증·회의실·참석자·반복 «동작»은 main 그대로다 — 배치와 겉모양만 바꿨다.
          */}
          <div className="calendar-modal-field-row">
            {/*
              2026-10-14 — 캘린더가 하나도 없어도 칸을 «보인다»(사용자 결정). 그때 고를 것은 「기본 캘린더」 하나 —
              사이드바의 「기본 캘린더」 통과 같은 이름이라 사용자가 둘을 잇는다. (종전: calendars.length > 0 일 때만)
            */}
            <div className="calendar-modal-field calendar-modal-field--half">
              <span className="calendar-modal-field-lbl" id="event-calendar-label">{tCalendar('calendar.create.calendar_field')}</span>
              <Combobox
                value={calendarId}
                onChange={(v) => setCalendarId(v)}
                options={[
                  { value: '', label: tCalendar('calendar.create.calendar_default') },
                  ...calendars.map((c) => ({ value: c.id, label: c.teamName ? `${c.teamName} · ${c.name}` : c.name })),
                ]}
                ariaLabel={tCalendar('calendar.create.calendar_field')}
                disabled={submitting}
              />
            </div>
            <div className="calendar-modal-field calendar-modal-field--half">
              <span className="calendar-modal-field-lbl" id="event-type-label">{tCalendar('calendar.event_create.label_type')}</span>
              <Combobox
                value={type}
                onChange={(v) => setType(v as CalendarEventType)}
                options={typeOptions}
                ariaLabel={tCalendar('calendar.event_create.label_type')}
                disabled={submitting}
              />
            </div>
          </div>
          {!!calendars.find((c) => c.id === calendarId)?.teamId && (
            <p className="calendar-modal-hint">{tCalendar('calendar.create.calendar_team_hint')}</p>
          )}

          {special ? (
            <div className="calendar-modal-field-row">
              <div className="calendar-modal-field">
                <label htmlFor="event-date">
                  {tCalendar(type === 'todo' ? 'calendar.event_create.label_due' : 'calendar.event_create.label_date')}
                </label>
                <DateTimePicker
                  id="event-date"
                  mode="date"
                  value={dateValue}
                  onChange={setDate}
                  accent={TYPE_COLOR_MAP[type]?.color}
                  required
                />
              </div>
              <div className="calendar-modal-field calendar-modal-field--inline">
                {type === 'anniversary' && (
                  <label>
                    <input type="checkbox" checked={repeatYearly} onChange={(e) => setRepeatYearly(e.target.checked)} disabled={isEdit} />
                    {' '}{tCalendar('calendar.anniversary.repeat_yearly')}
                  </label>
                )}
                {type === 'todo' && (
                  <label>
                    <input type="checkbox" checked={done} onChange={(e) => setDone(e.target.checked)} />
                    {' '}{tCalendar('calendar.event_create.label_done')}
                  </label>
                )}
              </div>
            </div>
          ) : (
            <>
              {/*
                시작/종료 — 「날짜」 상자 하나(시안 B), 누르면 #914 달력 창에서 시작·종료 날짜와 시간을 함께 고른다(EventRangePicker).
                  상태는 그대로 'YYYY-MM-DDTHH:mm' 두 문자열이다. 하루종일이면 날짜만 — 시각은 하루종일 시각(내 근태 시간 · 기본 09:00 ~ 18:00).
              */}
              <EventRangePicker
                startAt={startAt}
                endAt={endAt}
                isAllDay={isAllDay}
                onStartChange={moveStartKeepingLength}
                onEndChange={setEndAt}
                onAllDayChange={applyAllDay}
                accent={TYPE_COLOR_MAP[type]?.color}
                endFieldClassName={pointedEnd ? 'calendar-modal-field--pointed' : undefined}
                presets={pickerPresets}
                endLocked={endLocked}
                required
              />

              <div className="calendar-modal-field-row calendar-modal-field-row--toggles">
                <div className="calendar-modal-field calendar-modal-field--inline">
                  <label className="calendar-modal-toggle-label">
                    <input
                      type="checkbox"
                      checked={isAllDay}
                      onChange={(e) => applyAllDay(e.target.checked)}
                    />
                    {' '}{tCalendar('calendar.event_create.label_allday')}
                  </label>
                </div>
                <div className="calendar-modal-field calendar-modal-field--inline">
                  <label className="calendar-modal-toggle-label" htmlFor="event-recur">
                    <input
                      id="event-recur"
                      type="checkbox"
                      checked={recurFreq !== ''}
                      disabled={submitting || alreadyRecurring}
                      onChange={(e) => setRecur(e.target.checked
                        // 매주로 처음 켜면 시작 요일을 미리 고른다 — RecurrenceSettings 의 selectTab 과 같은 규칙(turnOnWeekly).
                        ? turnOnWeekly(recur, startAt)
                        : { ...recur, freq: '' })}
                    />
                    {' '}{tCalendar('calendar.recur.label')}
                  </label>
                </div>
              </div>

              {/*
                반복 — ⭐ 2026-10-13 이식: 생성·수정 모두(할일·기념일은 자체 처리). 4탭 · 요일 · 몇째 주/날짜 방식.
                ⚠ 이미 반복인 일정은 «끄지» 못하고 설정만 바꾼다 — 바꾸면 「이후 일정 모두」로만 저장된다.
                ⭐ 2026-10-14 — 「반복」 체크는 위 줄(하루종일 옆)에 두고, 여기서는 켜졌을 때 패널만 그린다.
              */}
              <RecurrenceSettings
                value={recur}
                onChange={setRecur}
                startLocal={startAt}
                disabled={submitting}
                legacyCount={legacyCount}
                hint={alreadyRecurring
                  ? `${tCalCreate('rc.edit_future_hint')}${ruleHasDateParts(editEvent?.recurrenceRule) ? ` ${tCalCreate('rc.date_rule_follows_hint')}` : ''}`
                  : undefined}
              />
            </>
          )}

          {/*
            장소 — 「회의실 예약」 카드를 켜면 회의실 목록(고른 방 이름이 그대로 장소), 끄면 장소를 직접 적는다.
            ⚠ 켠 채 방을 안 골랐으면 목록에 없는 곳을 적는 칸이 아래에 뜬다. 끄면 방 선택도 «되돌아간다»(`toggleRoom`).
            ★ 외부일정은 장소 칸이 카카오 장소 검색(LocationPicker)이다 — 이동시간이 이 문자열을 그대로 지오코딩한다.
          */}
          {!special && (
            <div className="calendar-modal-field">
              <div className="cal-lblrow">
                <span className="calendar-modal-field-lbl" id="event-location-label">{tCalendar('calendar.event_create.label_location')}</span>
                {!roomOn && <LengthCounter id="event-location-count" length={location.length} max={LOCATION_MAX} />}
              </div>
              <div className="calendar-optin-cards">
                <OptInCard
                  id="event-room"
                  on={roomOn}
                  onToggle={toggleRoom}
                  disabled={submitting || (roomTimePast && !roomOn)}
                  icon={<Building2 size={18} aria-hidden />}
                  label={tCalendar('calendar.room.opt_in')}
                />
              </div>
              {roomTimePast && !roomOn && (
                <p className="calendar-modal-hint calendar-room-past-hint" role="note">
                  {tCalendar('calendar.room.past_reason')} {tCalendar('calendar.room.past_action')}
                </p>
              )}
              {roomOn ? (
                <>
                  <p className="calendar-modal-hint">{tCalendar('calendar.room.hint_30min')}</p>
                  {roomsError && <CalendarErrorNotice className="calendar-modal-hint" error={roomsError} />}
                  {pickedRoom ? (
                    <RoomChoiceSummary
                      room={pickedRoom}
                      checkRange={roomCheckRange}
                      category={ROOM_CATEGORIES.length === 1 ? ROOM_CATEGORIES[0] : undefined}
                      ignoreReservationId={linkedReservation?.id ?? null}
                      past={roomTimePast}
                      allDay={isAllDay}
                      disabled={submitting}
                      onEdit={() => setSide('room')}
                    />
                  ) : meetingRooms && meetingRooms.length > 0 ? (
                    <p className="calendar-modal-hint calendar-choice-hint">
                      {side === 'room' ? tCalendar('calendar.room.side_hint') : (
                        <button type="button" className="calendar-choice-open" onClick={() => setSide('room')} disabled={submitting || !roomCheckRange}>
                          {tCalendar('calendar.room.side_open')}
                        </button>
                      )}
                    </p>
                  ) : null}
                  {roomId ? (
                    <p className="calendar-modal-hint">
                      {tCalendar('calendar.room.location_from_room', { location })}{' '}
                      {tCalendar('calendar.room.hint')}
                      {!isEdit && recurFreq ? ` ${tCalendar('calendar.room.hint_recurring')}` : ''}
                    </p>
                  ) : (
                    <input
                      id="event-location"
                      className="calendar-room-manual"
                      type="text"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder={tCalendar('calendar.event_create.placeholder_location')}
                      aria-labelledby="event-location-label"
                      maxLength={LOCATION_MAX}
                      disabled={submitting}
                    />
                  )}
                </>
              ) : type === 'external' ? (
                <LocationPicker
                  id="event-location"
                  value={location}
                  onChange={setLocation}
                  placeholder={tCalendar('calendar.event_create.placeholder_location_search')}
                  disabled={submitting}
                />
              ) : (
                <input
                  id="event-location"
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder={tCalendar('calendar.event_create.placeholder_location')}
                  aria-labelledby="event-location-label"
                  aria-describedby="event-location-count"
                  maxLength={LOCATION_MAX}
                />
              )}
            </div>
          )}

          {/*
            참석자 — 「참석자 초대」 카드를 켜야 고르개가 나온다.
            ⭐ 2026-10-15 — 수정 모드도 같은 고르개다(종전 «읽기 전용»은 PATCH 가 참석자를 받게 되어 걷었다).
          */}
          {!special && (
            <div className="calendar-modal-field">
              <span className="calendar-modal-field-lbl">{tCalendar('calendar.event_create.label_attendees')}</span>
              <div className="calendar-optin-cards">
                <OptInCard
                  id="event-attendees"
                  on={peopleOn}
                  onToggle={toggleAttendees}
                  disabled={submitting}
                  icon={<UserPlus size={18} aria-hidden />}
                  label={tCalendar('calendar.event_create.attendees_toggle')}
                />
              </div>
              {peopleOn && (
                orgError ? (
                  <p className="calendar-modal-hint">{tCalendar('calendar.event_create.attendees_load_failed')}</p>
                ) : (
                  <>
                    {/*
                      ⭐ 2026-10-19 — 고르기는 오른쪽 옆 창(AttendeePickerPanel)에서. 여기에는 고른 결과 + 「수정」만.
                        종전의 팀 초대 단추(TeamInviteButton)는 옆 창 위로 옮겼다.
                    */}
                    {hasOthers ? (
                      <div className="calendar-choice-summary" data-testid="attendee-choice-summary">
                        <span className="calendar-choice-avatars" aria-hidden>
                          {attendees.slice(0, 4).map((a) => (
                            <Avatar key={a.kind + ':' + a.id} userId={Number(a.id)} name={a.name} size="xs" />
                          ))}
                        </span>
                        <span className="calendar-choice-body">
                          <span className="calendar-choice-title">{tCalendar('calendar.attendee_panel.count', { n: attendeeUserIds.length })}</span>
                          <span className="calendar-choice-sub">{attendees.map((a) => a.name).join(', ')}</span>
                        </span>
                        <button type="button" className="calendar-choice-edit" onClick={() => { ensureOrgTree(); setSide('people'); }} disabled={submitting}>
                          {tCalendar('calendar.choice.edit')}
                        </button>
                      </div>
                    ) : extNow.length > 0 ? null : (
                      <p className="calendar-modal-hint calendar-choice-hint">
                        {side === 'people' ? tCalendar('calendar.attendee_panel.side_hint') : (
                          <>
                            {tCalendar('calendar.attendee_panel.only_me')}{' '}
                            <button type="button" className="calendar-choice-open" onClick={() => { ensureOrgTree(); setSide('people'); }} disabled={submitting}>
                              {tCalendar('calendar.attendee_panel.open')}
                            </button>
                          </>
                        )}
                      </p>
                    )}
                    {/* 수를 안 보여 주면 100명을 넘겨도 «줄일 방법»을 모른다. */}
                    <p className="calendar-modal-hint">
                      {tCalendar('calendar.detail.attendee_count', { n: attendeeUserIds.length })}
                      {' · '}
                      {tCalendar('calendar.event_create.attendees_me_only')}
                    </p>
                    {/*
                      ⭐ 2026-11-02 (지시 57) — 고른 외부 참석자. 직원과 구분해 「외부」 + 회사. 저장 체크 칸은 없다 —
                        저장하면 서버가 메일을 보낸다(그 사실과, 못 보내는 까닭만 한 줄씩).
                    */}
                    {extSupported && extNow.length > 0 && (
                      <div className="calendar-choice-summary calendar-choice-summary--external" data-testid="external-choice-summary">
                        <span className="calendar-choice-body">
                          <span className="calendar-choice-title">{tCalendar('calendar.external.summary', { n: extNow.length })}</span>
                          <span className="calendar-external-chips">
                            {extNow.map((p) => (
                              <span key={externalPickKey(p)} className="calendar-external-chip" data-testid="external-chip">
                                <span className="calendar-external-badge">{tCalendar('calendar.attendee_panel.external_badge')}</span>
                                <span className="calendar-external-chip-text">
                                  {externalDisplayName(p)}
                                  {p.company && <span className="calendar-external-chip-company"> · {p.company}</span>}
                                </span>
                              </span>
                            ))}
                          </span>
                        </span>
                        {!hasOthers && (
                          <button type="button" className="calendar-choice-edit" onClick={() => { ensureOrgTree(); setSide('people'); }} disabled={submitting}>
                            {tCalendar('calendar.choice.edit')}
                          </button>
                        )}
                      </div>
                    )}
                    {extSupported && extNow.length > 0 && (
                      <p className="calendar-modal-hint" data-testid="external-mail-hint">
                        {externalUnavailableText(extInfo) ?? tCalendar('calendar.external.mail_hint')}
                      </p>
                    )}
                    {extSupported && extSeriesNote && (extNow.length > 0 || (extOriginal?.length ?? 0) > 0) && (
                      <p className="calendar-modal-hint" data-testid="external-series-note">{extSeriesNote}</p>
                    )}
                  </>
                )
              )}
            </div>
          )}

          <div className="calendar-modal-field">
            <div className="cal-lblrow">
              <label htmlFor="event-description">{tCalendar('calendar.event_create.label_description')}</label>
              <LengthCounter id="event-description-count" length={description.length} max={DESCRIPTION_MAX} />
            </div>
            <AutoGrowTextarea
              id="event-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              aria-describedby="event-description-count"
              maxLength={DESCRIPTION_MAX}
            />
          </div>

          {!special && (
            <AttachmentUploader
              state={uploadState}
              disabled={submitting}
              label={tCalendar('calendar.event_create.attach_label')}
            />
          )}

          {error && <div className="calendar-modal-error" role="alert">{error}</div>}

          {/*
            ⛔ 못 누르는 단추는 «왜 못 누르는지» 보여야 한다 — 안 보이면 「고장났다」로 읽힌다.
            ★ 서버가 낼 말과 같은 뜻을 «저장 전»에 말한다(IN_THE_PAST).

            ⭐ **2026-10-02 — 「그럼 어쩌라고」로 끝나지 않게 한 걸음 더 간다.**
            ```
              종전: 한 줄에 까닭 ＋ 할 일 ＋ 다른 길을 다 담았다 ⇒ ⛔ 「끄면 되는 거였어?」로 읽힌다
              이제: ① 까닭 ② 할 일 ＋ ★ «그 칸으로 데려가는 단추» ③ 다른 길(옅게 떼어)
            ```
            ⚠ 앱(AOS)은 「시간 선택 «화면»으로 보낸다」인데, 웹은 그 칸이 **같은 창 안**이다.
              ⇒ ★ 그래서 «보내기»가 아니라 ⭐ «가리키기»다 — 초점을 옮기고 한 번 두드러지게 한다.
          */}
          {roomPastBlocked && (
            <div className="calendar-modal-error calendar-room-past" role="alert">
              <p className="calendar-room-past-reason">{tCalendar('calendar.room.past_reason')}</p>
              <p className="calendar-room-past-action">
                {tCalendar('calendar.room.past_action')}
                {' '}
                <button type="button" className="calendar-room-past-goto" onClick={pointAtEndTime}>
                  {tCalendar('calendar.room.past_goto_end')}
                </button>
              </p>
              {/* ⚠ 「다른 길」은 ★ 떼어 둔다 — 할 일과 같은 줄에 두면 이쪽이 더 쉬워 보인다. */}
              <p className="calendar-room-past-alt">{tCalendar('calendar.room.past_alt')}</p>
            </div>
          )}

          <footer className="calendar-modal-footer">
            <button type="button" onClick={handleClose} disabled={submitting}>{tCalendar('calendar.btn.cancel')}</button>
            <button type="submit" disabled={submitting || roomPastBlocked} className="calendar-modal-submit">
              {submitting ? tCalendar('calendar.btn.saving') : tCalendar('calendar.btn.save')}
            </button>
          </footer>
        </form>
      </div>
      {sidePanel && <div className={`calendar-side-dock${isLeaving ? ' calendar-modal--leaving' : ''}`}>{sidePanel}</div>}
      </div>
    </div>
  );
}

/*
 * ⛔ 종전의 `defaultStart`/`defaultEnd`(언제나 09:00~10:00)는 ★ `./default-event-range.ts` 로 옮겼다
 *   (2026-10-01). 옮긴 까닭·규칙·경계 셋은 그 파일 머리말에 있다 — 여기서 다시 세지 말 것.
 */

/**
 * 일정 제목 상한 — 화면은 50자(2026-10-14, 6100 화면에 맞춤 · 옛 가지 c59fee059). 서버는 200자를 그대로 받는다.
 * ⚠ 공용 `TITLE_MAX`(200, 게시판 등도 씀)는 건드리지 않고 이 창에서만 좁힌다.
 */
const EVENT_TITLE_MAX = 50;

/**
 * 하루종일 일정의 시각 — 근태 시간을 읽기 전·못 읽을 때의 기본 09:00 ~ 18:00 (all-day-work-hours.ts).
 * ⭐ 2026-10-20 사용자 결정 「하루종일 = 출근시간부터 퇴근시간까지」. 종전(2026-10-14)은 00:00 ~ 23:59.
 */
const ALL_DAY_START = ALL_DAY_FALLBACK.start;
const ALL_DAY_END = ALL_DAY_FALLBACK.end;

/** 'YYYY-MM-DDTHH:mm' → 날짜/시간 반쪽. 시작·종료가 날짜 피커 + 시간 피커 두 칸이라 둘이 각자 반쪽만 바꾼다. */
function splitDt(dtLocal: string): { date: string; time: string } {
  const [date = '', time = '00:00'] = dtLocal.split('T');
  return { date, time: time.slice(0, 5) || '00:00' };
}
function joinDt(date: string, time: string): string {
  return `${date}T${time}`;
}

/**
 * 「장소」의 회의실 예약 · 「참석자」의 초대 — 같은 켜고 끄는 카드 단추(`calendar-optin-card`, role=checkbox).
 * 두 자리가 글자·아이콘·id 만 달라 한 벌로 그린다(DOM 은 종전 그대로).
 */
function OptInCard({ id, on, onToggle, disabled, icon, label }: {
  id: string;
  on: boolean;
  onToggle: (next: boolean) => void;
  disabled: boolean;
  icon: ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      id={id}
      aria-checked={on}
      className={`calendar-optin-card${on ? ' is-on' : ''}`}
      onClick={() => onToggle(!on)}
      disabled={disabled}
    >
      {icon}
      <span>{label}</span>
      <span className="calendar-optin-check" aria-hidden><Check size={11} /></span>
    </button>
  );
}


